import { useState, useEffect, useRef } from "react";
import FlexxQuizResultPanel from "@/flexx/FlexxQuizResultPanel";
import { FlexxCertificate, openCertificatePrintView } from "@/flexx/FlexxCertificate";

const FLEXX_LOGO = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAACDQAAAXcCAYAAADXlEzmAAAAAXNSR0IArs4c6QAAAIRlWElmTU0AKgAAAAgABQEGAAMAAAABAAIAAAEaAAUAAAABAAAASgEbAAUAAAABAAAAUgEoAAMAAAABAAIAAIdpAAQAAAABAAAAWgAAAAAAAAErAAAAAQAAASsAAAABAAOgAQADAAAAAQABAACgAgAEAAAAAQAACDSgAwAEAAAAAQAABdwAAAAArE0U/AAAAAlwSFlzAAAt/AAALfwBrsPsmAAAA3RpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IlhNUCBDb3JlIDYuMC4wIj4KICAgPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4KICAgICAgPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIKICAgICAgICAgICAgeG1sbnM6ZGM9Imh0dHA6Ly9wdXJsLm9yZy9kYy9lbGVtZW50cy8xLjEvIgogICAgICAgICAgICB4bWxuczp0aWZmPSJodHRwOi8vbnMuYWRvYmUuY29tL3RpZmYvMS4wLyIKICAgICAgICAgICAgeG1sbnM6SXB0YzR4bXBFeHQ9Imh0dHA6Ly9pcHRjLm9yZy9zdGQvSXB0YzR4bXBFeHQvMjAwOC0wMi0yOS8iPgogICAgICAgICA8ZGM6dGl0bGU+CiAgICAgICAgICAgIDxyZGY6QWx0PgogICAgICAgICAgICAgICA8cmRmOmxpIHhtbDpsYW5nPSJ4LWRlZmF1bHQiPkZMRVhYIFdISVRFPC9yZGY6bGk+CiAgICAgICAgICAgIDwvcmRmOkFsdD4KICAgICAgICAgPC9kYzp0aXRsZT4KICAgICAgICAgPHRpZmY6WVJlc29sdXRpb24+Mjk5PC90aWZmOllSZXNvbHV0aW9uPgogICAgICAgICA8dGlmZjpYUmVzb2x1dGlvbj4yOTk8L3RpZmY6WFJlc29sdXRpb24+CiAgICAgICAgIDx0aWZmOlBob3RvbWV0cmljSW50ZXJwcmV0YXRpb24+MjwvdGlmZjpQaG90b21ldHJpY0ludGVycHJldGF0aW9uPgogICAgICAgICA8dGlmZjpSZXNvbHV0aW9uVW5pdD4yPC90aWZmOlJlc29sdXRpb25Vbml0PgogICAgICAgICA8SXB0YzR4bXBFeHQ6QXJ0d29ya1RpdGxlPkZMRVhYIFdISVRFPC9JcHRjNHhtcEV4dDpBcnR3b3JrVGl0bGU+CiAgICAgICAgIDwvcmRmOkRlc2NyaXB0aW9uPgogICAgICAgPC9yZGY6UkRGPgo8L3g6eG1wbWV0YT4=";

// ─── BRAND TOKENS ────────────────────────────────────────────────────────────
const B = {
  black:        "#000000",
  blackDeep:    "#060606",
  blackCard:    "#0C0C0C",
  blackPanel:   "#111111",
  blackBorder:  "#1C1C1C",
  blackHover:   "#161616",
  // CFD accent — deep electric teal/cyan (distinct from gold and blue)
  cfd:          "#00D4AA",
  cfdLight:     "#33FFCC",
  cfdDark:      "#008870",
  cfdGlow:      "rgba(0,212,170,0.12)",
  cfdFaint:     "rgba(0,212,170,0.06)",
  cfdWarn:      "#FF6B35",
  cfdWarnFaint: "rgba(255,107,53,0.08)",
  // Secondaries
  gold:         "#C9A227",
  blue:         "#4A9EFF",
  white:        "#FFFFFF",
  whiteOff:     "#EDE8DC",
  whiteGray:    "#AAAAAA",
  whiteMuted:   "#555555",
  whiteDim:     "#333333",
};

// ─── COMPLETE CFD CURRICULUM ──────────────────────────────────────────────────
const COURSE = {
  id: "cfd",
  title: "CFD MASTERY",
  subtitle: "Contracts for Difference — The Professional's Instrument",
  tagline: "Institutional-grade CFD education · Built by FLEXX Academy · Dubai 2026",
  badge: "FILE 3 OF 4",
  description: "CFDs (Contracts for Difference) are the instrument used by most professional retail traders worldwide to trade stocks, indices, commodities, forex, and crypto — all from a single account, with leverage, and the ability to profit in BOTH directions. This course takes you from zero understanding of what a CFD is to advanced multi-asset portfolio construction.",
  levels: [
    {
      id: "foundation",
      label: "FOUNDATION",
      tag: "WHAT IS A CFD?",
      color: B.cfd,
      icon: "◈",
      desc: "You've never heard of a CFD or you're not 100% clear on how they work. We start from first principles — no prior knowledge assumed.",
      phases: [
        {
          id: "f1",
          label: "CFD Fundamentals",
          icon: "01",
          weeks: "Week 1–2",
          desc: "What a CFD actually is, how the mechanics work, and why professional traders prefer them over buying actual assets.",
          modules: [
            {
              id: "f1-1",
              title: "What Is a Contract for Difference (CFD)?",
              duration: "25 min",
              tag: "FOUNDATION",
              type: "concept",
              summary: "A CFD is a financial contract between you and a broker where you agree to exchange the DIFFERENCE in price of an asset from when you open a position to when you close it. You never own the underlying asset — no stock certificates, no crypto wallets, no oil barrels. You simply profit or lose based on price movement. This simple concept unlocks access to thousands of global markets from one account.",
              keyPoints: [
                "CFD = Contract for DIFFERENCE — you profit from the price change, not ownership",
                "LONG (BUY) CFD: You profit when the price RISES. Your gain = (Close Price − Entry Price) × Contracts",
                "SHORT (SELL) CFD: You profit when the price FALLS. Your gain = (Entry Price − Close Price) × Contracts",
                "You NEVER own the underlying asset — no delivery, no wallet, no custody needed",
                "Available on: stocks (Apple, Jollibee), indices (SPX, NAS100), forex (EUR/USD), commodities (gold, oil), crypto (BTC)",
                "All from ONE account, ONE platform — this is the CFD's most powerful advantage",
                "CFDs use LEVERAGE — you control a large position with a small deposit (margin)",
              ],
              whyItMatters: "Understanding that you never own the underlying asset is the key mindset shift. You're trading the PRICE, not the asset. This means you can profit when Tesla falls, when gold drops, when a currency weakens — not just when things go up.",
              practice: "Open IC Markets, IG Markets, or CMC Markets demo account. Find the following CFDs: (1) Apple stock, (2) SPX500 index, (3) Gold, (4) EUR/USD forex, (5) Bitcoin. Notice they're ALL on the same platform. This is the CFD advantage.",
              resources: [
                { label: "What Is A CFD? — Complete Beginner Guide 2024", url: "https://www.youtube.com/results?search_query=what+is+a+CFD+contract+for+difference+explained+beginners+2024", type: "▶ VIDEO", tag: "FREE — WATCH FIRST" },
                { label: "Investopedia: Contracts For Difference (CFD) Definition", url: "https://www.investopedia.com/terms/c/contractfordifferences.asp", type: "📄 READ", tag: "FREE" },
                { label: "ASIC: CFD Explained (Australian Regulator)", url: "https://www.moneysmart.gov.au/investing/complex-investments/contracts-for-difference-cfds", type: "📄 READ", tag: "REGULATED" },
              ],
            },
            {
              id: "f1-2",
              title: "Long vs Short — Profiting in Both Directions",
              duration: "30 min",
              tag: "FOUNDATION",
              type: "concept",
              summary: "The most powerful feature of CFDs is the ability to SHORT — to profit when prices FALL. This is impossible with regular stock investing (unless you understand complex short selling). With CFDs, going short is as simple as clicking 'SELL'. This single feature doubles your opportunities in any market condition.",
              keyPoints: [
                "GOING LONG: Click BUY → profit when price rises → lose when price falls",
                "GOING SHORT: Click SELL → profit when price falls → lose when price rises",
                "Example LONG: Buy Apple CFD at $150. Apple goes to $170. Profit = $20 per share × contracts",
                "Example SHORT: Sell SPX CFD at 5000. SPX falls to 4800. Profit = 200 points × contract size",
                "BEAR MARKETS are opportunities, not disasters — CFD traders love volatility in BOTH directions",
                "Short selling through CFDs has NO uptick rule, NO hard-to-borrow fees unlike stock short selling",
                "2024-2025 Reality: Professional traders use shorts to hedge their portfolio during corrections",
              ],
              whyItMatters: "During the 2022 bear market, stock investors lost 20-40%. CFD traders who shorted made exceptional returns. Understanding both directions is not optional — it's what separates professional traders from buy-and-hold investors.",
              practice: "On your demo account: (1) Open a LONG position on any index CFD, (2) Open a SHORT position on the same index, (3) Watch them move in opposite directions simultaneously. This builds intuition for directional trading.",
              resources: [
                { label: "Short Selling CFDs Explained — Full Tutorial", url: "https://www.youtube.com/results?search_query=short+selling+CFD+trading+explained+how+to+profit+falling+markets+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "CFD Trading Example — Long vs Short Calculation", url: "https://www.youtube.com/results?search_query=CFD+trading+example+long+short+profit+loss+calculation+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "f1-3",
              title: "Margin & Leverage — The Math Behind CFDs",
              duration: "40 min",
              tag: "CRITICAL",
              type: "risk",
              summary: "Leverage is the defining characteristic of CFDs — and the most dangerous if misunderstood. With 1:10 leverage, $1,000 controls a $10,000 position. A 1% move becomes a 10% gain or loss on your account. The math must be internalized before any real money is at risk.",
              keyPoints: [
                "MARGIN = the deposit required to open a leveraged position (typically 1%–20% of position value)",
                "LEVERAGE = how much you control relative to your margin deposit",
                "Example: 1:10 leverage on Apple CFDs, buy $10,000 worth → margin required = $1,000",
                "If Apple rises 2%: Position gains $200 = 20% return on your $1,000 margin",
                "If Apple falls 2%: Position loses $200 = 20% loss on your $1,000 margin",
                "MARGIN CALL: When losses consume your margin, broker closes position automatically",
                "OVERNIGHT FINANCING (Swap): Holding leveraged positions overnight costs daily interest — track this",
                "RULE: Never use more than 1:5 effective leverage as a beginner. Most accounts offer 1:20 or more — IGNORE it",
              ],
              whyItMatters: "The most common CFD blowup story: trader uses 1:20 leverage, market moves 5% against them, account is wiped. With 1:5 leverage and a proper stop loss, a 5% adverse move = only a 25% drawdown (recoverable). Leverage is a tool, not a game.",
              dangerZone: "Retail CFD traders lose money primarily because they over-leverage. ESMA (European regulator) limits retail leverage to 1:2–1:30 specifically because 74%+ of retail CFD accounts lose money. Use leverage as a tool for capital efficiency, never to bet larger than your risk management allows.",
              practice: "Calculate: You have $5,000 account. You want to buy Tesla CFD at $200/share. You risk 1% = $50. Your stop loss is $10 below entry ($190). How many CFD contracts? Answer: $50 ÷ $10 = 5 contracts. Position value = 5 × $200 = $1,000. At 1:10 leverage, margin required = $100.",
              resources: [
                { label: "CFD Margin & Leverage Calculator — Live", url: "https://www.myfxbook.com/forex-calculators/margin", type: "⚙ TOOL", tag: "FREE" },
                { label: "CFD Leverage Explained — The Real Risk", url: "https://www.youtube.com/results?search_query=CFD+leverage+margin+explained+risk+management+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "Overnight Financing (Swap) — What It Costs to Hold CFDs", url: "https://www.youtube.com/results?search_query=CFD+overnight+financing+swap+cost+holding+position+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "f1-4",
              title: "CFD vs Real Stock / Crypto / Futures — When to Use What",
              duration: "30 min",
              tag: "FOUNDATION",
              type: "concept",
              summary: "CFDs are not always the right instrument. Understanding when CFDs are the best choice vs actual stock ownership vs futures vs options vs spot crypto is a professional-level decision that directly impacts costs, risk, and opportunity. Each instrument has specific advantages.",
              keyPoints: [
                "CFD ADVANTAGES: Leverage, short-selling ease, multi-asset access, no ownership costs (storage, custody)",
                "CFD DISADVANTAGES: Overnight financing costs compound over time; you own NO assets; counterparty risk with broker",
                "REAL STOCK BUY advantages: Dividends, voting rights, long-term ownership value, no overnight costs",
                "REAL STOCK BUY disadvantages: No leverage, can't easily short, capital-intensive",
                "FUTURES advantages: Exchange-regulated, no counterparty risk, standardized; disadvantages: fixed contract sizes, expiry dates",
                "SPOT CRYPTO advantages: True ownership, 24/7, DeFi access; disadvantages: no leverage on regulated platforms (in Philippines), custody risk",
                "RULE OF THUMB: CFDs for short-to-medium term trading (days to weeks). Real stocks/crypto for long-term investing (years)",
              ],
              whyItMatters: "A professional trader holds actual BTC as a long-term investment AND uses BTC CFDs for short-term trades. They may own Apple stock long-term AND short Apple via CFD during earnings season. Understanding the tool distinction is the sign of a mature trader.",
              practice: "For the same asset (e.g., Apple stock), compare: (1) Buying 10 actual shares vs (2) Buying 10 share CFDs with 1:5 leverage. What's the total capital needed each way? What's the overnight cost of holding the CFD for 30 days at your broker's rate?",
              resources: [
                { label: "CFD vs Stocks vs Futures vs Options — Key Differences", url: "https://www.youtube.com/results?search_query=CFD+vs+stocks+vs+futures+options+which+is+better+trading+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "Investopedia: CFD vs Options", url: "https://www.investopedia.com/articles/active-trading/110714/getting-started-cfds.asp", type: "📄 READ", tag: "FREE" },
              ],
            },
            {
              id: "f1-5",
              title: "Choosing Your CFD Broker — The Non-Negotiable Checklist",
              duration: "35 min",
              tag: "PRACTICAL",
              type: "setup",
              summary: "Not all CFD brokers are equal. Some are regulated and safe. Others are unregulated 'bucket shops' designed to take your money. The broker choice is one of the most important decisions you'll make — a bad broker can manipulate spreads, freeze withdrawals, or simply disappear with your capital.",
              keyPoints: [
                "REGULATION is mandatory — accept NOTHING less. Tier 1: FCA (UK), ASIC (Australia), MAS (Singapore)",
                "ACCOUNT TYPE: ECN/STP (Electronic Communication Network) = broker passes your orders to real market. Market Maker = broker takes opposite side of your trade (conflict of interest)",
                "SPREADS: The bid-ask spread is your transaction cost. ECN brokers have tighter spreads + small commission. Compare across brokers for your main instruments",
                "MINIMUM DEPOSIT: $100–$500 is reasonable for beginners. Anything under $50 = suspect",
                "PLATFORMS: MetaTrader 4/5 (industry standard), cTrader (ECN-focused), proprietary (varies)",
                "WITHDRAWAL: Test with small withdrawal before depositing serious capital — never deposit what you can't afford to lose with unverified broker",
                "RECOMMENDED (Philippines-accessible): IC Markets (ASIC), Pepperstone (ASIC/FCA), XM (multi-regulated), Plus500 (FCA)",
              ],
              dangerZone: "Red flags: Promises of guaranteed returns, pressure to deposit more, platforms you can't withdraw from easily, no verifiable regulation. NEVER use a broker not regulated by Tier 1 or Tier 2 authority.",
              practice: "Compare IC Markets vs Pepperstone vs XM on: (1) Regulation tier, (2) Account type (ECN/STP vs MM), (3) EUR/USD spread, (4) Overnight financing rate for long stock CFDs, (5) Minimum deposit. Build a comparison table.",
              resources: [
                { label: "IC Markets — ASIC Regulated ECN Broker", url: "https://www.icmarkets.com/", type: "⚙ BROKER", tag: "RECOMMENDED" },
                { label: "Pepperstone — FCA/ASIC Regulated", url: "https://www.pepperstone.com/", type: "⚙ BROKER", tag: "RECOMMENDED" },
                { label: "How to Choose A CFD Broker — Full Checklist 2024", url: "https://www.youtube.com/results?search_query=how+to+choose+best+CFD+broker+regulated+2024+beginner", type: "▶ VIDEO", tag: "FREE" },
                { label: "BrokerChooser — Independent Broker Comparison", url: "https://brokerchooser.com/best-brokers/cfd-trading", type: "⚙ TOOL", tag: "FREE" },
              ],
            },
          ],
        },
        {
          id: "f2",
          label: "CFD Risk Architecture",
          icon: "02",
          weeks: "Week 2–3",
          desc: "Before you trade a single CFD live, your risk framework must be bulletproof. CFDs amplify everything — wins and losses alike.",
          modules: [
            {
              id: "f2-1",
              title: "The 1% Rule Applied to CFDs — Position Sizing with Leverage",
              duration: "35 min",
              tag: "CRITICAL",
              type: "risk",
              summary: "Position sizing for CFDs is more complex than for stocks or crypto because leverage changes the relationship between position size and account risk. The formula must account for: (1) account risk amount, (2) stop loss distance in price terms, (3) CFD contract size, and (4) leverage factor. Get this wrong and one trade can destroy months of gains.",
              keyPoints: [
                "BASE RULE: Never risk more than 1% of total account per trade — identical to all other markets",
                "CFD FORMULA: Number of Contracts = Account Risk ÷ (Stop Distance × Contract Value)",
                "Example: $10,000 account, 1% risk = $100. Stop = 20 points. SPX CFD contract value = $1/point",
                "Contracts = $100 ÷ (20 × $1) = 5 contracts. Position value = 5 × current SPX price",
                "LEVERAGE CHECK: If position value ÷ margin required > 10x → reduce contracts",
                "Stock CFDs: Contract = 1 share (usually). Indices: varies ($1/pt, $10/pt — check broker spec)",
                "Commodities: Gold = 100 oz/contract (standard). Oil = 1000 barrels. Always verify before trading",
                "MAXIMUM OPEN RISK: Sum of all open trade risks ≤ 5% of account at any one time",
              ],
              whyItMatters: "This formula prevents over-leveraging even when your broker offers 1:20 leverage. By anchoring to your dollar risk amount rather than position size, you automatically scale positions correctly regardless of leverage.",
              practice: "Calculate position sizes for each scenario:\n(1) $5,000 account, 1% risk, Long NAS100 CFD at 18,000, stop at 17,800 (200pts), $1/pt contract\n(2) $10,000 account, 1% risk, Short Gold CFD at $2,000, stop at $2,020 ($20 move), 100oz contract\n(3) $3,000 account, 1% risk, Long Apple CFD at $180, stop at $175 ($5 move), 1 share contract",
              resources: [
                { label: "CFD Position Size Calculator — Automated", url: "https://www.myfxbook.com/forex-calculators/position-size", type: "⚙ TOOL", tag: "FREE" },
                { label: "CFD Position Sizing — Correct Formula Explained", url: "https://www.youtube.com/results?search_query=CFD+position+sizing+formula+correct+calculation+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "f2-2",
              title: "Stop Losses, Gaps & Slippage — CFD-Specific Risks",
              duration: "35 min",
              tag: "CRITICAL",
              type: "risk",
              summary: "Stop losses in CFD trading have unique risks not present in spot markets. Markets gap open (price jumps past your stop), and high leverage means even small slippage can significantly impact your account. Understanding these CFD-specific risks and having protection strategies is non-negotiable.",
              keyPoints: [
                "GAPPING: If you hold overnight, markets can open FAR past your stop — you close at a worse price",
                "Example: You short SPX with stop at 5050. Positive economic data releases overnight. SPX opens at 5120. Your stop executes at 5120, not 5050 — 70 points of SLIPPAGE",
                "GUARANTEED STOP-LOSS ORDERS (GSLOs): Some brokers offer these for a small premium — your stop ALWAYS executes at your level, even through gaps. Worth it for overnight positions",
                "SLIPPAGE: During fast markets (news releases, open/close), your stop may execute a few points away from your set level. Normal < 2-3 points. Abnormal > 10 points (consider changing broker)",
                "CLOSE BEFORE MAJOR EVENTS: Don't hold CFD positions through NFP, CPI, FOMC if your stop could be gapped",
                "TRAILING STOP strategy: As position moves in your favor, move stop to lock in profits — protects against reversal",
                "Weekend risk: Market-hours positions held over weekend face Monday gap risk — reduce size Friday afternoon",
              ],
              dangerZone: "Gap risk is the PRIMARY reason professional CFD traders avoid holding large leveraged positions over weekends or major news events. A 3% gap against a 1:10 leveraged position = 30% account loss in one open candle.",
              practice: "On TradingView, look at the SPX daily chart. Find 5 examples of overnight gaps (where the opening price differs significantly from previous close). How many were UP gaps vs DOWN gaps? How large were they in points?",
              resources: [
                { label: "CFD Gap Risk — How to Protect Your Account", url: "https://www.youtube.com/results?search_query=CFD+gap+risk+overnight+how+to+protect+account+trading+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "Guaranteed Stop-Loss Orders — Are They Worth It?", url: "https://www.youtube.com/results?search_query=guaranteed+stop+loss+CFD+GSLO+worth+it+explained+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "f2-3",
              title: "Overnight Financing Costs — The Hidden Drag on CFD Profits",
              duration: "25 min",
              tag: "COST MANAGEMENT",
              type: "risk",
              summary: "Every CFD position held beyond the market's close accrues overnight financing (also called 'swap' or 'rollover' cost). This is the interest on the leveraged portion of your position. For short-term trades, it's negligible. For multi-week holds, it can eat significantly into profits — especially on leveraged stock CFDs.",
              keyPoints: [
                "FORMULA: Overnight cost = (Position Value × Annual Rate) ÷ 365",
                "Example: 10 Apple CFDs ($180/share) = $1,800 position. Rate = 5% annual. Daily cost = $1,800 × 0.05 ÷ 365 = $0.25/day",
                "At 1:10 leverage: Only $180 margin. But you're charged on the FULL $1,800 position value",
                "LONG positions: Usually CHARGED overnight (you pay the financing rate)",
                "SHORT positions: Sometimes RECEIVE overnight (you receive interest on the short proceeds — but not always)",
                "TRIPLE SWAP: On Wednesday night, most brokers charge 3x normal swap (to account for weekend)",
                "For MULTI-WEEK holds: Calculate total financing cost. If > 10% of expected profit, consider real stock ownership instead of CFD",
              ],
              whyItMatters: "A trader holding 100 Apple CFDs ($18,000 position) for 30 days at 5% annual rate pays: $18,000 × 0.05 / 365 × 30 = $73.97 in financing. On a 2% expected move ($360 profit), that's 20% of the profit consumed by costs. Know your costs before you trade.",
              practice: "On your chosen broker's platform, find the swap/overnight rate for: (1) Long SPX500 CFD, (2) Long Gold CFD, (3) Long Apple stock CFD. For a $10,000 position in each, calculate the daily and monthly financing cost.",
              resources: [
                { label: "CFD Overnight Financing Calculator", url: "https://www.myfxbook.com/forex-calculators/swap", type: "⚙ TOOL", tag: "FREE" },
                { label: "CFD Overnight Swap Cost Explained Simply", url: "https://www.youtube.com/results?search_query=CFD+overnight+financing+swap+cost+explained+how+much+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "core",
      label: "CORE SYSTEM",
      tag: "TECHNICAL EDGE",
      color: B.cfd,
      icon: "◆",
      desc: "The technical framework for trading CFDs across all asset classes. Market structure, entries, targets, and the multi-asset advantage.",
      phases: [
        {
          id: "c1",
          label: "Market Structure for CFDs",
          icon: "01",
          weeks: "Week 4–6",
          desc: "The same institutional market structure framework from your Forex/Crypto training — now applied specifically to the CFD context across all asset classes.",
          modules: [
            {
              id: "c1-1",
              title: "Universal Market Structure — Works on ALL CFD Markets",
              duration: "45 min",
              tag: "CORE 20%",
              type: "analysis",
              summary: "The most powerful insight in CFD trading: market structure works IDENTICALLY across all asset classes. The same BOS (Break of Structure), CHoCH (Change of Character), swing highs and lows that work on EUR/USD forex also work on Gold CFDs, SPX CFDs, Apple stock CFDs, and Bitcoin CFDs. Master it once, apply it everywhere.",
              keyPoints: [
                "UPTREND: Higher Highs + Higher Lows → BUY BIAS on all instruments",
                "DOWNTREND: Lower Lows + Lower Highs → SELL BIAS (short CFDs) on all instruments",
                "BOS (Break of Structure): Confirms trend continuation — trade in breakout direction",
                "CHoCH (Change of Character): First warning the trend may be reversing — tighten stops",
                "TOP-DOWN: Weekly bias → Daily structure → 4H entry zones → 1H/15M precision entry",
                "CFD-SPECIFIC: Stock indices (SPX, NAS100) are EASIER to read — less noise than individual stocks",
                "Commodities (Gold, Oil): Have seasonal patterns AND market structure — combine both",
                "Forex pairs: Market structure PLUS fundamental (interest rate) bias for highest probability",
              ],
              whyItMatters: "Once you internalize this framework, you can trade ANY CFD market. You're not learning 'how to trade gold' separately from 'how to trade indices.' You're learning ONE language that every market speaks.",
              practice: "On TradingView, pull up: Gold (XAUUSD), SPX500, Apple (AAPL), EUR/USD. On the DAILY chart for each one, mark every swing high and swing low for the past 3 months. Which ones are in uptrend? Downtrend? Sideways? This is your current market map.",
              resources: [
                { label: "Market Structure Applied to Stocks, Indices & Commodities", url: "https://www.youtube.com/results?search_query=market+structure+stocks+indices+commodities+CFD+2024+SMC", type: "▶ VIDEO", tag: "FREE" },
                { label: "ICT Concepts Applied to Stock Market CFDs", url: "https://www.youtube.com/results?search_query=ICT+concepts+stock+market+indices+how+to+apply+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "c1-2",
              title: "Asset-Specific Behaviors — What Makes Each Market Unique",
              duration: "50 min",
              tag: "CORE 20%",
              type: "analysis",
              summary: "While market structure is universal, each asset class has UNIQUE behaviors, key drivers, and session characteristics. Knowing these behavioral profiles is what allows professionals to trade multiple assets simultaneously without getting confused. This is the CFD trader's portfolio intelligence.",
              keyPoints: [
                "STOCK INDICES (SPX, NAS100): Most influenced by Fed policy, tech earnings, employment data. Bullish long-term bias over decades. Best during NY session (8pm–5am MNL). Avoid FOMC, CPI days",
                "GOLD (XAUUSD): 'Safe haven' — rises when fear rises, falls when confidence returns. Driven by USD strength (inverse), inflation expectations, geopolitical tension. Most volatile during London-NY overlap",
                "OIL (WTI/BRENT): Driven by OPEC decisions, US inventory reports (Wed 10:30pm MNL), global demand growth. High correlation to risk-on/risk-off sentiment. Seasonal patterns (summer driving, winter heating)",
                "STOCK CFDs: Individual stocks move on: quarterly earnings, CEO changes, product launches, analyst ratings. AVOID holding through earnings unless very experienced",
                "FOREX CFDs: Interest rate differentials, central bank meetings, economic data. USD is the ANCHOR — understand USD first",
                "CRYPTO CFDs: Bitcoin dominance cycle drives rotation. On-chain data adds edge. 24/7 but most volume 8pm–5am MNL",
              ],
              whyItMatters: "A professional CFD trader runs a portfolio: Long Gold (hedging index risk), Long NAS100 (tech trend), Short specific overvalued stock, Long EUR/USD (rate differential). They DON'T treat each instrument in isolation.",
              practice: "Create a one-page 'Asset Intelligence Sheet' for each of these CFD markets: SPX500, Gold, Oil, EUR/USD, Bitcoin. For each: (1) Main driver, (2) Best trading session, (3) Avoid trading when..., (4) Current trend (your analysis).",
              resources: [
                { label: "Gold Market Analysis — What Drives XAUUSD 2024", url: "https://www.youtube.com/results?search_query=gold+market+analysis+what+drives+XAUUSD+2024+drivers", type: "▶ VIDEO", tag: "FREE" },
                { label: "Oil Trading Fundamentals — OPEC, Inventory Reports", url: "https://www.youtube.com/results?search_query=oil+trading+fundamentals+OPEC+inventory+WTI+Brent+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "Stock Index CFD Trading — SPX vs NAS100 Differences", url: "https://www.youtube.com/results?search_query=stock+index+CFD+trading+SPX+NAS100+differences+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "c1-3",
              title: "Order Blocks & Liquidity on CFDs — Institutional Footprints",
              duration: "50 min",
              tag: "CORE 20%",
              type: "analysis",
              summary: "Order blocks and liquidity pools (from the ICT/SMC framework covered in the Forex course) apply with full force to CFD markets. In fact, they're often MORE visible on stock index CFDs (SPX, NAS100) because institutional order flow is concentrated and leaves clear footprints.",
              keyPoints: [
                "ORDER BLOCK (OB) on Indices: Last opposing candle before a strong institutional move. Price returns to this zone to fill remaining orders",
                "BULLISH OB on SPX: Last red candle before a strong upward push — price returns to this candle's range before continuing up",
                "BEARISH OB on Gold: Last green candle before a strong downward push — institutional selling zone",
                "LIQUIDITY SWEEPS on Stocks: Equal highs/lows on individual stock CFDs get swept before major moves (earnings catalyst provides the sweep)",
                "FAIR VALUE GAPS (FVG): 3-candle gap on any CFD — fill rate on indices is extremely high (80%+ on SPX daily)",
                "PREMIUM vs DISCOUNT ARRAYS: Buy in discount (below equilibrium), sell in premium (above equilibrium) — universal across all CFDs",
                "SPX DAILY FVGs: Some of the most reliable FVGs in any market — institutional program trading creates them consistently",
              ],
              practice: "On NAS100 or SPX500 daily chart, identify the last 5 Bullish and 5 Bearish Order Blocks. For each one that price returned to test, did it hold? Mark each as: (1) Held & reversed, (2) Partially held, (3) Failed. This builds your OB confidence calibration.",
              resources: [
                { label: "ICT Order Blocks on Stock Indices — Full Tutorial", url: "https://www.youtube.com/results?search_query=ICT+order+blocks+NAS100+SPX+stock+indices+CFD+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "Fair Value Gaps on SPX500 — High Probability Entries", url: "https://www.youtube.com/results?search_query=fair+value+gap+SPX500+NAS100+high+probability+entry+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
          ],
        },
        {
          id: "c2",
          label: "CFD Trading Strategies",
          icon: "02",
          weeks: "Week 6–9",
          desc: "Specific, battle-tested CFD strategies for indices, commodities, and stock CFDs. Each with defined entry rules, stop placement, and profit targets.",
          modules: [
            {
              id: "c2-1",
              title: "Strategy 1 — Index CFD: The NY Open Setup (Opening Drive)",
              duration: "55 min",
              tag: "STRATEGY",
              type: "strategy",
              summary: "The New York session open (8:30pm Manila time) is the single most powerful daily event for US index CFDs (SPX500, NAS100, US30). The 'Opening Drive' strategy captures the initial institutional momentum that establishes the direction for the entire session. This is a high-frequency, high-probability setup used by professional intraday CFD traders.",
              keyPoints: [
                "SETUP TIME: 8:00–8:30pm MNL (pre-market) + 8:30pm MNL (NY cash open)",
                "STEP 1: Identify D1 bias (is the index bullish or bearish on daily chart?)",
                "STEP 2: At 8pm, mark the Asian session range HIGH and LOW",
                "STEP 3: Pre-market sweep? If price sweeps below Asian low before NY open → potential LONG setup. If sweeps above Asian high → potential SHORT",
                "STEP 4: At 8:30pm NY open, look for strong directional candle in D1 bias direction with volume",
                "STEP 5: Enter on 5M chart pullback to FVG or OB within first 30 minutes of NY session",
                "TARGET: Previous day's high/low or daily FVG above/below",
                "STOP: Below the NY open swing low (for longs) or above swing high (for shorts)",
              ],
              whyItMatters: "The NY open creates the highest volume of the trading day for US indices. Institutional program trading, market-on-open orders, and fund rebalancing all execute at this time. The initial direction often holds for 1–3 hours — enough time for a quality trade.",
              practice: "Paper trade: For 10 consecutive trading days (Mon–Fri), observe the NAS100 or SPX500 at 8:30pm MNL. Record: (1) Pre-market direction of Asian range sweep, (2) NY open direction, (3) Did it follow through 1 hour? 2 hours? Build your statistical data before risking real money.",
              resources: [
                { label: "ICT NY Open Session — Full Strategy Guide 2024", url: "https://www.youtube.com/results?search_query=ICT+New+York+open+session+strategy+indices+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "Opening Drive Strategy — Index CFD NAS100 SPX", url: "https://www.youtube.com/results?search_query=opening+drive+strategy+NAS100+SPX+CFD+intraday+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "c2-2",
              title: "Strategy 2 — Gold CFD: The Institutional Range & Reversal",
              duration: "50 min",
              tag: "STRATEGY",
              type: "strategy",
              summary: "Gold (XAUUSD) is the most widely traded commodity CFD in the world. Its behavior is highly predictable once you understand the institutional range — the zone between the Asian session high and low. Gold repeatedly returns to this range before making its big directional move during London and NY sessions.",
              keyPoints: [
                "GOLD BEHAVIOR: Forms a range during Asian session (7am–3pm MNL), then breaks directionally during London open (3pm MNL)",
                "SETUP TYPE 1 — Range Reversal: Price spikes above/below Asian range → look for bearish/bullish OB entry for reversal back into range",
                "SETUP TYPE 2 — Range Breakout: After consolidation, identify direction of break → enter on first pullback to broken level",
                "KEY GOLD DRIVERS to check daily: USD index (DXY) direction, US 10-Year yield direction, VIX level",
                "INVERSE CORRELATION: DXY rising = Gold usually falling. DXY falling = Gold usually rising (not always, check actual price action)",
                "GOLD FVGs: Extremely reliable on 1H and 4H. Mark daily FVGs and trade fills",
                "STOP PLACEMENT: Below the key reversal candle's wick + 5 points buffer. Gold wicks can be wide — size accordingly",
                "TARGET: Previous session's high/low, key daily FVG, major round numbers ($2000, $2100, $2500)",
              ],
              practice: "Backtest: On gold (XAUUSD) 1H chart, go back 3 months. Mark every Asian session range. Record: (1) How many times did gold spike above/below the range and reverse?, (2) How many times did it break directionally and continue? Build your personal statistics.",
              resources: [
                { label: "Gold CFD Trading Strategy — Professional Approach 2024", url: "https://www.youtube.com/results?search_query=gold+XAUUSD+CFD+trading+strategy+professional+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "DXY vs Gold Correlation — Understanding the Relationship", url: "https://www.youtube.com/results?search_query=DXY+dollar+index+gold+correlation+trading+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "TradingView — Gold CFD Live Chart", url: "https://www.tradingview.com/chart/?symbol=OANDA%3AXAUUSD", type: "⚙ TOOL", tag: "FREE" },
              ],
            },
            {
              id: "c2-3",
              title: "Strategy 3 — Stock CFD: Earnings Plays & Post-Announcement Structure",
              duration: "60 min",
              tag: "STRATEGY",
              type: "strategy",
              summary: "Individual stock CFDs offer unique opportunities around earnings announcements. The professional approach is NOT to hold through earnings (too risky) — it's to trade the POST-EARNINGS market structure that forms after the initial reaction settles. This is one of the most consistent setups in CFD trading.",
              keyPoints: [
                "EARNINGS DATE RULE: Never hold a stock CFD THROUGH the earnings announcement — the gap risk is unquantifiable",
                "POST-EARNINGS PLAY (1–3 days after): After the initial reaction gap, the stock establishes new market structure",
                "POSITIVE EARNINGS GAP UP: Wait for first pullback to fill the gap's FVG → enter LONG with tight stop below gap",
                "NEGATIVE EARNINGS GAP DOWN: Wait for first pullback to test the gap resistance → enter SHORT with stop above gap",
                "SETUP CONFIRMATION: Must see higher timeframe structure supporting direction BEFORE entry",
                "STOP: Below the FVG/OB used for entry — if the fill reverses completely, the thesis is wrong",
                "HIGH-IMPACT EARNINGS (know these): Apple (AAPL), Nvidia (NVDA), Meta (META), Google (GOOGL) — these move the indices too",
                "PHILIPPINE STOCKS: Jollibee (JFC), BDO, SM Prime — apply same post-earnings structure framework on PSE",
              ],
              dangerZone: "Holding stock CFDs through earnings is gambling, not trading. Stocks regularly gap 10-20% on earnings. With 1:5 leverage, a 15% gap against you wipes 75% of your margin. Close positions before earnings, re-enter after.",
              practice: "Pick the next major tech earnings (check earningswhispers.com). BEFORE earnings: Mark key support/resistance levels. AFTER earnings: Wait 24 hours. Identify the new market structure. Plan the post-earnings CFD trade (paper only for now).",
              resources: [
                { label: "Earnings Calendar — Know What's Coming", url: "https://earningswhispers.com/calendar", type: "⚙ TOOL", tag: "FREE" },
                { label: "Trading Stock CFDs Around Earnings — Professional Approach", url: "https://www.youtube.com/results?search_query=trading+stock+CFD+around+earnings+professional+strategy+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "Post-Earnings Market Structure — How to Trade The Gap", url: "https://www.youtube.com/results?search_query=post+earnings+gap+market+structure+trading+strategy+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "c2-4",
              title: "Strategy 4 — Short Selling CFDs: Bear Market Profits",
              duration: "45 min",
              tag: "STRATEGY",
              type: "strategy",
              summary: "The ability to short CFDs is your most powerful edge over stock investors. When markets enter distribution phases, corrections, or full bear markets, CFD traders don't panic — they position SHORT and profit from the decline. This strategy covers the specific framework for shorting markets professionally.",
              keyPoints: [
                "SHORT SIGNAL — MULTIPLE TIMEFRAME BEARISH ALIGNMENT: Weekly downtrend + Daily downtrend + 4H distribution → SHORT BIAS confirmed",
                "HIGH PROBABILITY SHORT SETUP: CHoCH on daily (first sign of trend reversal) + Bearish OB or FVG retest on 4H + Volume declining on rallies",
                "SECTOR WEAKNESS: Short the weakest sector/stock in a general market downturn (highest probability shorts)",
                "SHORT ENTRY RULE: Only short on RALLIES (pullbacks to resistance), not into existing drops — this gives best R:R",
                "STOP PLACEMENT for shorts: Above the bearish OB + small buffer. If price reclaims the OB, the short thesis is invalid",
                "TARGET: Previous support levels, lower FVGs, round number supports",
                "BEAR MARKET PORTFOLIO: When D1 on SPX shows clear downtrend → short individual weak stocks instead of buy dips on indices",
              ],
              whyItMatters: "2022 bear market: SPX fell 25%, NAS100 fell 35%. Every stock investor suffered. Professional CFD traders who shorted: NAS100 short from 15,000 to 10,000 = 5,000 points profit. This is why CFDs change everything.",
              practice: "Look at the NAS100 chart during 2022. Identify: (1) When did CHoCH appear on daily? (2) Where were the first bearish OBs to short? (3) What were the Fibonacci extension targets to the downside? This is historical learning — build your short-trade eye.",
              resources: [
                { label: "Short Selling Strategy — Bear Market CFD Profits", url: "https://www.youtube.com/results?search_query=short+selling+strategy+bear+market+CFD+professional+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "How to Find the Weakest Stocks to Short in a Downtrend", url: "https://www.youtube.com/results?search_query=how+to+find+weakest+stocks+to+short+in+downtrend+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "advanced",
      label: "ADVANCED",
      tag: "PORTFOLIO LEVEL",
      color: B.gold,
      icon: "◉",
      desc: "Multi-asset CFD portfolio management. Hedging, correlation trading, and building an income-generating trading operation.",
      phases: [
        {
          id: "a1",
          label: "Multi-Asset CFD Portfolio",
          icon: "01",
          weeks: "Week 10–14",
          desc: "Treating your CFD trading as a portfolio of uncorrelated positions across multiple markets — the professional way to manage risk and maximize opportunity.",
          modules: [
            {
              id: "a1-1",
              title: "Portfolio Thinking — Diversified CFD Position Management",
              duration: "60 min",
              tag: "ADVANCED",
              type: "portfolio",
              summary: "Advanced CFD traders don't trade one market at a time — they maintain a portfolio of positions across multiple markets simultaneously. The key is CORRELATION AWARENESS: ensuring your positions aren't all exposed to the same risk factor. A professional CFD portfolio is designed so that not all positions can lose at the same time.",
              keyPoints: [
                "CORRELATION RISK: If you're long SPX, long NAS100, AND long individual tech stocks — you have 100% correlation. One macro event wipes everything",
                "UNCORRELATED PAIRS: Long stocks + Long Gold (Gold is inversely correlated to stocks during fear events) = natural hedge",
                "RISK-ON positions: Indices, growth stocks, oil (global demand), AUD/USD, commodity currencies",
                "RISK-OFF positions: Gold, JPY (Japanese Yen), CHF (Swiss Franc), US Treasuries (bond ETF CFDs)",
                "PORTFOLIO CONSTRUCTION RULE: Max 3 highly correlated positions at once. Always have at least 1 'safe haven' position as hedge",
                "TOTAL PORTFOLIO RISK: Sum all positions' dollar risk. Should never exceed 5-8% of account at one time",
                "WINNING PORTFOLIO EXAMPLE: Long NAS100 (tech trend), Long XAUUSD (hedge + trend), Short weak energy stock (sector rotation), Long EUR/USD (rate differential)",
              ],
              whyItMatters: "In October 2023, tech stocks fell AND gold rose simultaneously. A trader long BOTH had a NATURAL HEDGE — the gold profits partially offset the stock losses. This is how professionals survive corrections while staying positioned for the next move up.",
              practice: "Look at the correlation between these pairs over the past 1 year on TradingView: (1) SPX500 vs Gold: correlated or inverse? (2) SPX500 vs DXY (Dollar): correlated or inverse? (3) Gold vs DXY: correlated or inverse? These correlations are your portfolio architecture tools.",
              resources: [
                { label: "Asset Correlation Table — Finviz", url: "https://finviz.com/correlations.ashx", type: "⚙ TOOL", tag: "FREE" },
                { label: "Multi-Asset CFD Portfolio Strategy — Professional", url: "https://www.youtube.com/results?search_query=multi+asset+CFD+portfolio+strategy+professional+diversified+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "a1-2",
              title: "Hedging With CFDs — Protecting Your Investment Portfolio",
              duration: "55 min",
              tag: "ADVANCED",
              type: "portfolio",
              summary: "One of the most powerful and often overlooked CFD uses: hedging your existing investment portfolio. If you hold actual stocks or crypto that you believe in long-term but are worried about a short-term correction, you can use SHORT CFD positions to hedge — effectively neutralizing downside risk without selling your actual assets.",
              keyPoints: [
                "HEDGE SCENARIO: You hold ₱500,000 of Philippine stocks long-term but fear a correction",
                "CFD HEDGE: Short PSEi or related stock CFDs for an amount roughly equal to your stock portfolio value",
                "If market falls 10%: Your stock portfolio loses ₱50,000 but your short CFDs GAIN approximately ₱50,000 — net loss ≈ 0",
                "DELTA HEDGING: Match your hedge ratio to your exposure. If you're 50% invested, hedge 50% via CFDs",
                "COST OF HEDGING: Your short CFD hedge has overnight financing cost — calculate this as 'insurance premium'",
                "REMOVING THE HEDGE: When your fundamental reason for the hedge passes (correction complete, news resolved), close CFD short positions",
                "CRYPTO HEDGE EXAMPLE: Hold actual BTC long-term. Worried about short-term drop. Open short BTC CFD for partial hedge amount. BTC drops → CFD gains offset. BTC rises → You capture upside minus small CFD loss",
              ],
              whyItMatters: "Philippine investors who held stocks through the 2020 COVID crash lost 40%+ in weeks. A CFD hedge positioned at the time they noticed market deterioration could have reduced losses to near zero — while keeping their long-term holdings intact.",
              practice: "Paper exercise: You hold ₱300,000 of PSEi stocks. Market shows signs of topping (CHoCH on weekly). Calculate: (1) How many PSEi index CFD contracts to SHORT to hedge your portfolio?, (2) What's the daily financing cost of holding this hedge?, (3) What's your net P&L if PSEi falls 15%?",
              resources: [
                { label: "Hedging Your Stock Portfolio With CFDs — Complete Guide", url: "https://www.youtube.com/results?search_query=hedging+stock+portfolio+with+CFDs+complete+guide+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "CFD Hedging Strategy — Practical Examples", url: "https://www.youtube.com/results?search_query=CFD+hedging+strategy+practical+examples+portfolio+protection", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "a1-3",
              title: "Sector Rotation Trading With Stock CFDs",
              duration: "50 min",
              tag: "ADVANCED",
              type: "portfolio",
              summary: "Sector rotation is the movement of institutional money between market sectors based on the economic cycle. Understanding where we are in the cycle — and which sectors are flowing IN vs OUT — allows you to LONG the strongest sector CFDs and SHORT the weakest simultaneously. This is a market-neutral, high-probability strategy.",
              keyPoints: [
                "ECONOMIC CYCLE: Early expansion → Late expansion → Contraction → Recovery. Each stage favors different sectors",
                "EARLY CYCLE winners: Financials (XLF), Consumer Discretionary (XLY), Technology (XLK)",
                "LATE CYCLE winners: Energy (XLE), Materials (XLB), Industrials (XLI)",
                "DEFENSIVE sectors (recession): Utilities (XLU), Consumer Staples (XLP), Healthcare (XLV)",
                "SECTOR ROTATION SIGNALS: Relative strength of each sector ETF vs SPX benchmark. Rising RS = money flowing in. Falling RS = money flowing out",
                "CFD APPLICATION: Long the top 2 rising RS sectors. Short the bottom 2 falling RS sectors. Profit from SPREAD between strong and weak",
                "TOOLS: Finviz sector heatmap, TradingView sector performance, MarketWatch sector performance",
              ],
              practice: "Visit Finviz.com → Groups → Sectors. Look at 1-month and 3-month performance for each S&P 500 sector. Which sectors have the highest relative strength? Which have the lowest? Based on this, what LONG and SHORT CFD trade would a sector rotation trader currently have on?",
              resources: [
                { label: "Sector Rotation Strategy — Market Cycle Guide 2024", url: "https://www.youtube.com/results?search_query=sector+rotation+strategy+market+cycle+trading+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "Finviz Sector Heatmap — Real Time", url: "https://finviz.com/map.ashx?t=sec&st=1w", type: "⚙ TOOL", tag: "FREE" },
                { label: "StockCharts RRG — Relative Rotation Graphs for Sectors", url: "https://stockcharts.com/freecharts/rrg.html", type: "⚙ TOOL", tag: "FREE" },
              ],
            },
          ],
        },
        {
          id: "a2",
          label: "Advanced Risk & Psychology",
          icon: "02",
          weeks: "Week 14–16",
          desc: "Drawdown management, trading psychology at the advanced level, and building a sustainable CFD business.",
          modules: [
            {
              id: "a2-1",
              title: "Drawdown Recovery — What to Do When You're Losing",
              duration: "45 min",
              tag: "ADVANCED",
              type: "psychology",
              summary: "Every trader — including the best in the world — goes through drawdowns. What separates professionals from amateurs is not avoiding drawdowns (impossible) but having a documented RECOVERY PROTOCOL that prevents drawdowns from becoming account blowups. This module is the psychology and mechanics of surviving and recovering.",
              keyPoints: [
                "DRAWDOWN DEFINITION: The peak-to-trough decline in your account equity. 10% drawdown = you're currently 10% below your all-time high",
                "EXPECTED DRAWDOWN: Even with a 60% win rate and 2:1 R:R, you can have streaks of 6-8 consecutive losses. This is NORMAL",
                "DRAWDOWN PROTOCOL — 5% drawdown: Reduce all position sizes by 50%. Review last 10 trades. Are you following your rules?",
                "DRAWDOWN PROTOCOL — 10% drawdown: STOP TRADING for 5 business days. Paper trade only. Perform complete journal review",
                "DRAWDOWN PROTOCOL — 15% drawdown: Take 2 week break. Revisit fundamental strategy assumptions. Consider mentorship",
                "RECOVERY MINDSET: Do NOT try to 'make back losses quickly' — this is how 5% drawdowns become 50% drawdowns",
                "REDUCE SIZE on recovery: Come back with 50% of normal size until you've rebuilt 50% of the drawdown",
                "STATISTICS: Most prop trading firms set maximum drawdown at 10-15%. They force traders to stop because THEY know what happens next",
              ],
              whyItMatters: "The ONLY way to blow up a trading account with proper risk management is to BREAK your rules during a drawdown. The pressure to 'make it back' triggers revenge trading and over-leveraging. A written protocol that you follow BEFORE the drawdown happens is your most valuable document.",
              practice: "Write your personal Drawdown Protocol RIGHT NOW — before you're in a drawdown. Define: At what % drawdown do you halve position size? At what % do you stop trading? Who do you call/message? What do you review? Sign it and put it on your trading desk.",
              resources: [
                { label: "Drawdown Recovery — How Professional Traders Survive Losing Streaks", url: "https://www.youtube.com/results?search_query=trading+drawdown+recovery+protocol+professional+how+to+survive+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "Mark Douglas: Trading Psychology — The Mental Edge", url: "https://www.youtube.com/results?search_query=Mark+Douglas+trading+in+the+zone+psychology+mental+edge", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
            {
              id: "a2-2",
              title: "Building Your CFD Trading Business — Systems, Routines & Scale",
              duration: "55 min",
              tag: "ADVANCED",
              type: "business",
              summary: "Treating CFD trading as a BUSINESS — not a hobby or a gamble — is the mindset shift that separates consistently profitable traders from the majority who quit. A trading business has: a defined operating procedure, daily routines, documented edge, performance metrics, and a scale-up plan based on evidence, not emotion.",
              keyPoints: [
                "DAILY ROUTINE (pre-session): Check macro calendar (ForexFactory), review open positions, identify top 3 setups from Watchlist",
                "SESSION ROUTINE: Trade your plan, journal entries in real-time, no social media/news during active trading",
                "POST-SESSION ROUTINE: Mark up charts with what happened, record all P&L, update journal, review discipline (did you follow rules?)",
                "WEEKLY REVIEW: Statistical analysis of Win%, Average R:R, Best performing setup, Worst performing setup",
                "MONTHLY BUSINESS REVIEW: Profit/Loss statement, drawdown status, strategy adjustment if needed",
                "SCALE-UP CRITERIA: Only increase position size AFTER 3 consecutive profitable months. Never increase during a drawdown",
                "INCOME TARGET SETTING: Start with % target (10% monthly) not $ target. 10% on ₱50,000 = ₱5,000. As account grows, same % = larger income",
                "PROP TRADING CONSIDERATION: Once consistently profitable, evaluate funded account programs (FTMO, MyForexFunds, Apex) — trade institutional capital",
              ],
              whyItMatters: "Most traders who blow accounts do so not because of bad strategy but because of no system — no routine, no rules, no review. The business framework turns an emotional endeavor into a repeatable, scalable process.",
              practice: "Design your weekly trading schedule: (1) Which days/sessions will you trade? (2) When is your pre-session prep? (3) When is your weekly review? (4) What metrics will you track monthly? Write this in a calendar and commit to it for 30 days.",
              resources: [
                { label: "FTMO — Funded Trader Program (Prop Trading)", url: "https://ftmo.com/", type: "⚙ TOOL", tag: "EVALUATE AFTER 6 MONTHS" },
                { label: "How to Create a Trading Business Plan", url: "https://www.youtube.com/results?search_query=trading+business+plan+routines+system+professional+trader+2024", type: "▶ VIDEO", tag: "FREE" },
                { label: "Tradezella — Professional Trade Journal", url: "https://www.tradezella.com/", type: "⚙ TOOL", tag: "RECOMMENDED" },
              ],
            },
            {
              id: "a2-3",
              title: "CFD Tax Considerations — Philippines & International",
              duration: "30 min",
              tag: "LEGAL & TAX",
              type: "business",
              summary: "Tax treatment of CFD profits varies significantly by jurisdiction. In the Philippines, the tax treatment of financial instrument trading income is evolving. This module gives a framework for thinking about CFD taxes — always consult a qualified accountant for your specific situation.",
              keyPoints: [
                "PHILIPPINES: Trading income from financial instruments is generally treated as ordinary income subject to income tax",
                "BIR REGISTRATION: If you generate consistent trading income, you may need to register as a self-employed individual or sole proprietor",
                "RECORD KEEPING: Keep ALL trade records — broker statements, transaction history, P&L reports. This is your tax documentation",
                "FOREIGN BROKERS: Income from foreign CFD brokers (IC Markets, Pepperstone) may need to be declared as foreign-sourced income",
                "LOSSES: In many jurisdictions, capital/trading losses can offset gains — proper record-keeping enables this",
                "CORPORATE STRUCTURE: At significant profit levels, some traders set up a One Person Corporation (OPC) in the Philippines for tax efficiency",
                "DISCLAIMER: This is NOT tax advice. Consult a CPA familiar with financial markets trading in the Philippines",
              ],
              practice: "Action items: (1) Set up a separate trading expense spreadsheet (software, data, education costs may be deductible), (2) Enable transaction export in your broker platform, (3) Consult with a CPA if your annual trading income exceeds ₱250,000",
              resources: [
                { label: "BIR Tax Guide Philippines — Self-Employed Income", url: "https://www.bir.gov.ph/", type: "⚙ TOOL", tag: "OFFICIAL" },
                { label: "Philippines Trading Income Tax — What You Need to Know", url: "https://www.youtube.com/results?search_query=Philippines+trading+income+tax+BIR+self+employed+2024", type: "▶ VIDEO", tag: "FREE" },
              ],
            },
          ],
        },
      ],
    },
  ],
};

// ─── FLATTEN ALL MODULES ───────────────────────────────────────────────────────
function flattenModules(course) {
  const out = [];
  course.levels.forEach(lv =>
    lv.phases.forEach(ph =>
      ph.modules.forEach(m => out.push({ ...m, levelId: lv.id, phaseId: ph.id }))
    )
  );
  return out;
}

const ALL_MODS = flattenModules(COURSE);

// ─── COMPONENTS ───────────────────────────────────────────────────────────────


const GoldBar = ({ color = B.cfd }) => (
  <div style={{
    height: 2,
    background: `linear-gradient(90deg, transparent, ${color}, ${color}, transparent)`,
  }} />
);

const Tag = ({ children, color = B.cfd, size = 8 }) => (
  <span style={{
    fontSize: size,
    padding: "2px 8px",
    letterSpacing: 1.2,
    border: `1px solid ${color}40`,
    color: color,
    fontFamily: "monospace",
    flexShrink: 0,
  }}>{children}</span>
);

const TypeBadge = ({ type }) => {
  const config = {
    concept: { label: "CONCEPT", color: B.cfd },
    risk: { label: "⚠ RISK", color: B.cfdWarn },
    strategy: { label: "◈ STRATEGY", color: "#9B59B6" },
    analysis: { label: "ANALYSIS", color: B.blue },
    setup: { label: "SETUP", color: B.gold },
    portfolio: { label: "PORTFOLIO", color: "#E91E8C" },
    psychology: { label: "MINDSET", color: "#FF9800" },
    business: { label: "BUSINESS", color: "#8BC34A" },
  };
  const c = config[type] || config.concept;
  return <Tag color={c.color}>{c.label}</Tag>;
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
// ─── SAGEMASTER PRODUCT SUITE ─────────────────────────────────────────────────
const SAGEMASTER_PRODUCTS = [
  {
    id: "ai-screener",
    name: "AI Screener",
    icon: "🔍",
    tagline: "Find the best setups across 100+ assets in seconds",
    desc: "AI-powered scanner that filters the entire market to surface only high-probability setups matching your criteria. No more manually scanning charts one by one.",
    useCase: "Run every Sunday before the week opens. Filter by: EMA stack aligned + ADX > 25 + volume spike. This becomes your weekly FLEXX SCAN watchlist automatically.",
    url: "https://sagemaster.com",
    tag: "WEEKLY SCAN",
    color: "#4A9EFF",
  },
  {
    id: "deepsage",
    name: "DeepSage AI Analysis",
    icon: "🧠",
    tagline: "Institutional-grade AI market analysis on demand",
    desc: "Deep AI analysis engine that reads market structure, sentiment, and pattern data across multiple timeframes. Think of it as a junior analyst who never sleeps.",
    useCase: "Before entering any major trade, run DeepSage on the asset. Cross-reference its reading with your FLEXX checklist. If both align — high conviction. If they disagree — wait.",
    url: "https://sagemaster.com",
    tag: "PRE-TRADE",
    color: "#C9A227",
  },
  {
    id: "helix",
    name: "Helix",
    icon: "🌀",
    tagline: "Advanced pattern recognition + entry signal engine",
    desc: "Identifies complex chart patterns, FCS channels, and Elliott Wave structures automatically. Saves hours of manual charting work.",
    useCase: "Use Helix to validate your FCS channel entries. If Helix confirms the same E1/E2/E3 entry zone you identified manually, your confidence in the trade increases significantly.",
    url: "https://sagemaster.com",
    tag: "ENTRY CONFIRMATION",
    color: "#9B59B6",
  },
  {
    id: "ai-grid",
    name: "AI Grid",
    icon: "⚡",
    tagline: "Automated grid trading for ranging market conditions",
    desc: "Deploys intelligent grid strategies during sideways/ranging markets. While trend-following setups rest, AI Grid harvests the chop profitably.",
    useCase: "When ADX is below 20 (no trend), activate AI Grid on stable pairs like BTC/USDT or ETH/USDT. Set upper and lower bounds at key S/R levels. Let it work while you focus on learning.",
    url: "https://sagemaster.com",
    tag: "RANGING MARKETS",
    color: "#E67E22",
  },
  {
    id: "ai-dca",
    name: "AI DCA",
    icon: "📊",
    tagline: "Smart dollar-cost averaging with AI-optimized entry timing",
    desc: "Not all DCA is equal. AI DCA identifies statistically optimal entry points within your DCA schedule — buying more when conditions are most favorable, less when they're not.",
    useCase: "Perfect for long-term crypto positions (BTC, ETH). Set a monthly budget, let AI DCA distribute entries across the month at optimal price points instead of fixed calendar dates.",
    url: "https://sagemaster.com",
    tag: "LONG-TERM ACCUMULATION",
    color: "#2ECC71",
  },
  {
    id: "ai-omni",
    name: "AI Omni",
    icon: "🌐",
    tagline: "All-in-one multi-strategy AI trading system",
    desc: "The flagship tool. Combines screening, pattern recognition, entry signals, and risk management into a unified AI trading framework.",
    useCase: "Intermediate to advanced use. Once you've mastered the FLEXX manual system and understand WHY each signal fires, migrate your best setups into AI Omni for systematic execution.",
    url: "https://sagemaster.com",
    tag: "ADVANCED · FLAGSHIP",
    color: "#C9A227",
  },
  {
    id: "ai-strategy-pack",
    name: "AI Strategy Pack",
    icon: "📦",
    tagline: "Pre-built, battle-tested trading strategy templates",
    desc: "Library of ready-to-deploy trading strategies covering trend-following, momentum, mean-reversion, and breakout approaches — all validated with historical data.",
    useCase: "Start with the 'FLEXX EMA Stack' strategy template. Deploy it in Demo mode first. Run 50 trades. Check your hit ratio and edge ratio. Then decide if it's ready for live capital.",
    url: "https://sagemaster.com",
    tag: "STRATEGY TEMPLATES",
    color: "#4A9EFF",
  },
  {
    id: "intelligent-backtest",
    name: "Intelligent Backtest",
    icon: "📈",
    tagline: "Test any strategy against years of historical data instantly",
    desc: "Before putting real money on any strategy, backtest it across 1-5 years of historical data. See win rate, max drawdown, and expected returns before risking a cent.",
    useCase: "Rule: Never trade a strategy live that hasn't been backtested with minimum 100 trades. Use Intelligent Backtest to validate your FLEXX entries. 40%+ win rate + 2x edge = deployable.",
    url: "https://sagemaster.com",
    tag: "VALIDATION ESSENTIAL",
    color: "#E74C3C",
  },
  {
    id: "magic-terminal",
    name: "Magic Terminal",
    icon: "⌨️",
    tagline: "Professional order execution + trade management terminal",
    desc: "Execute trades with precision. Advanced order types, OCO orders, bracket orders, and automated trail stops. The professional's execution environment.",
    useCase: "Once your strategy is validated, use Magic Terminal for all live entries. Set your stop + target + trail stop in one click at entry time. This removes all emotional decision-making during the trade.",
    url: "https://sagemaster.com",
    tag: "LIVE EXECUTION",
    color: "#1ABC9C",
  },
];


// ─── ONBOARDING QUIZ ───
const ONBOARDING_QUIZ = [
  {
    id: "q1",
    question: "How would you describe your trading experience right now?",
    options: [
      { label: "Total beginner — I've never placed a real trade", value: "beginner" },
      { label: "Some experience — I've dabbled but lost money or don't have a system", value: "beginner" },
      { label: "Intermediate — I understand charts and have a partial strategy", value: "intermediate" },
      { label: "Advanced — I have a strategy but want to refine it with AI/SMC tools", value: "intermediate" },
    ],
  },
  {
    id: "q2",
    question: "What's your primary market interest?",
    options: [
      { label: "Crypto (Bitcoin, Altcoins, DeFi)", value: "crypto" },
      { label: "Stocks & Indices (NASDAQ, S&P 500)", value: "crypto" },
      { label: "Forex (EUR/USD, GBP/USD, JPY pairs)", value: "forex" },
      { label: "I want to learn all markets", value: "crypto" },
    ],
  },
  {
    id: "q3",
    question: "What's your biggest challenge right now?",
    options: [
      { label: "I don't know where to start or what to study", value: "beginner" },
      { label: "I know some things but I lose money consistently", value: "beginner" },
      { label: "I have entries but can't hold trades or manage exits", value: "intermediate" },
      { label: "I want to add AI screening and algorithmic tools to my arsenal", value: "intermediate" },
    ],
  },
];


// ─── MODULE QUIZZES ───
const MODULE_QUIZZES = {
  "cfd_1": {
    moduleTitle: "CFD Fundamentals Quiz",
    questions: [
      { q: "What does CFD stand for?", options: ["Controlled Financial Derivative","Contract For Difference","Capital Funded Deal","Compound Fixed Dividend"], correct: 1 },
      { q: "CFDs let you profit from price...", options: ["Only rises","Only falls","Both rises and falls","Neither"], correct: 2 },
      { q: "Leverage in CFDs...", options: ["Reduces risk","Amplifies both gains and losses","Only amplifies gains","Has no effect"], correct: 1 },
      { q: "Overnight CFD positions typically incur...", options: ["No cost","Swap/financing fees","Extra commission","Margin release"], correct: 1 },
    ],
  },
};

export default function FlexxCFD() {
  const [level, setLevel] = useState("foundation");
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [view, setView] = useState("map"); // map | lesson | glossary
  const [activeLesson, setActiveLesson] = useState(null);
  const [completed, setCompleted] = useState({});
  const [notes, setNotes] = useState({});
  const [noteInput, setNoteInput] = useState("");
  const [expandedSection, setExpandedSection] = useState(null);
  const [glossaryFilter, setGlossaryFilter] = useState("");
  const [pulse, setPulse] = useState(false);

  // ── v5 features state ──
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [onboardingAnswers, setOnboardingAnswers] = useState({});
  const [onboardingResult, setOnboardingResult] = useState(null);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [showCert, setShowCert] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [quizPassed, setQuizPassed] = useState({});
  const [streakData, setStreakData] = useState({ streak: 0, lastVisit: null, totalDays: 0 });
  const [sageMasterOpen, setSageMasterOpen] = useState(false);
  const [sageMasterProduct, setSageMasterProduct] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [lbName, setLbName] = useState("");
  const [lbSubmitted, setLbSubmitted] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [adminPwInput, setAdminPwInput] = useState("");
  const [adminPwError, setAdminPwError] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [videoUrls, setVideoUrls] = useState({});
  const [adminVideoEditing, setAdminVideoEditing] = useState(null);
  const [adminVideoInput, setAdminVideoInput] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncCode, setSyncCode] = useState("");
  const [syncImportInput, setSyncImportInput] = useState("");
  const [syncImportError, setSyncImportError] = useState("");
  const [syncImportSuccess, setSyncImportSuccess] = useState(false);
  const [threads, setThreads] = useState({});
  const [threadInput, setThreadInput] = useState("");
  const [threadAuthor, setThreadAuthor] = useState("");
  const [affiliateCode, setAffiliateCode] = useState("");



  // ── localStorage persistence ──
  useEffect(() => {
    try {
      const saved = localStorage.getItem("flexx_cfd_v5");
      if (saved) {
        const d = JSON.parse(saved);
        if (d.completed) setCompleted(d.completed);
        if (d.notes) setNotes(d.notes);
        if (d.quizPassed) setQuizPassed(d.quizPassed);
        if (d.studentName) setStudentName(d.studentName);
        if (d.onboardingDone) { setOnboardingDone(true); setOnboardingResult(d.onboardingResult); }
        if (d.level) setLevel(d.level);
        if (d.videoUrls) setVideoUrls(d.videoUrls);
        if (d.lbName) setLbName(d.lbName);
        if (d.lbSubmitted) setLbSubmitted(d.lbSubmitted);
        if (d.affiliateCode) setAffiliateCode(d.affiliateCode);
        if (d.threadAuthor) setThreadAuthor(d.threadAuthor);
      }
      const lb = localStorage.getItem("flexx_cfd_v5_lb");
      if (lb) setLeaderboardData(JSON.parse(lb));
      const th = localStorage.getItem("flexx_cfd_v5_threads");
      if (th) setThreads(JSON.parse(th));
    } catch(e) {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("flexx_cfd_v5", JSON.stringify({
        completed, notes, quizPassed, studentName, onboardingDone, onboardingResult,
        level, videoUrls, lbName, lbSubmitted, affiliateCode, threadAuthor,
      }));
    } catch(e) {}
  }, [completed, notes, quizPassed, studentName, onboardingDone, onboardingResult, level, videoUrls, lbName, lbSubmitted, affiliateCode, threadAuthor]);

  // ── Streak ──
  useEffect(() => {
    try {
      const today = new Date().toDateString();
      const saved = JSON.parse(localStorage.getItem("flexx_cfd_v5_streak") || "{}");
      const last = saved.lastVisit;
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      let streak = saved.streak || 0;
      let totalDays = saved.totalDays || 0;
      if (last !== today) {
        totalDays += 1;
        if (last === yesterday) streak += 1; else streak = 1;
        const updated = { streak, lastVisit: today, totalDays };
        localStorage.setItem("flexx_cfd_v5_streak", JSON.stringify(updated));
        setStreakData(updated);
      } else { setStreakData({ streak, lastVisit: today, totalDays: saved.totalDays || 1 }); }
    } catch(e) { setStreakData({ streak: 1, lastVisit: new Date().toDateString(), totalDays: 1 }); }
  }, []);

  // ── Onboarding ──
  const handleOnboardingAnswer = (qId, val) => {
    const next = { ...onboardingAnswers, [qId]: val };
    setOnboardingAnswers(next);
    if (onboardingStep < ONBOARDING_QUIZ.length - 1) setOnboardingStep(s => s + 1);
    else { setOnboardingResult({ level: "foundation" }); setOnboardingDone(true); }
  };

  // ── Module quiz ──
  const startQuiz = (moduleId, moduleTitle) => {
    if (!MODULE_QUIZZES[moduleId]) return;
    setActiveQuiz({ moduleId, moduleTitle });
    setQuizStep(0); setQuizAnswers({}); setQuizResult(null);
  };
  const handleQuizAnswer = (qIdx, ansIdx) => {
    const next = { ...quizAnswers, [qIdx]: ansIdx };
    setQuizAnswers(next);
    const quiz = MODULE_QUIZZES[activeQuiz.moduleId];
    if (qIdx < quiz.questions.length - 1) setQuizStep(s => s + 1);
    else {
      const total = quiz.questions.length;
      const score = quiz.questions.reduce((a, q, i) => a + (next[i] === q.correct ? 1 : 0), 0);
      const passed = score >= Math.ceil(total * 0.75);
      setQuizResult({ passed, score, total });
      if (passed) { setQuizPassed(p => ({ ...p, [activeQuiz.moduleId]: true })); toggleDone(activeQuiz.moduleId); }
    }
  };

  // ── Cert ──
  const generateCert = async (name, moduleTitle, trackLabel) => {
    const date = new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" });
    const studentDisplayName = name || "Student";
    setShowCert({ name: studentDisplayName, moduleTitle, date, track: trackLabel, affiliate: affiliateCode });
    try {
      const token = localStorage.getItem("flexx_access_token");
      if (!token) return;
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const axios = (await import("axios")).default;
      const { data } = await axios.post(`${API}/flexx/certificate/issue-course`, {
        student_name: studentDisplayName,
        course_name: moduleTitle,
        tier: "intermediate",
        track: trackLabel || "",
        course_subtitle: `${trackLabel || "FLEXX ACADEMY"} — a specialized component of the FLEXX ACADEMY Elite Trading Series`,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (data && data.cert_id) {
        setShowCert((prev) => prev ? { ...prev, verifyCode: data.cert_id, signature: data.signature } : prev);
      }
    } catch (e) {
       
      console.warn("Course cert issuance fell back to local code:", e?.response?.status || e?.message);
    }
  };
  // ── Cert canvas ──

  // ── Leaderboard ──
  const submitToLeaderboard = () => {
    if (!lbName.trim()) return;
    const entry = {
      name: lbName.trim(), completed: completedCount, total: totalMods, pct: progressPct,
      streak: streakData.streak,
      quizzes: Object.values(quizPassed).filter(Boolean).length,
      track: "CFD",
      date: new Date().toLocaleDateString("en-US", { month:"short", day:"numeric" }),
    };
    try {
      const existing = JSON.parse(localStorage.getItem("flexx_cfd_v5_lb") || "[]");
      const filtered = existing.filter(e => e.name !== entry.name);
      const updated = [entry, ...filtered].sort((a,b) => b.pct - a.pct || b.streak - a.streak).slice(0, 20);
      localStorage.setItem("flexx_cfd_v5_lb", JSON.stringify(updated));
      setLeaderboardData(updated); setLbSubmitted(true);
    } catch(e) {}
  };

  // ── Admin ──
  const ADMIN_PW = "flexx2026";
  const handleAdminLogin = () => {
    if (adminPwInput === ADMIN_PW) {
      setAdminMode(true); setShowAdminLogin(false);
      setAdminPwInput(""); setAdminPwError(false); setView("admin");
    } else { setAdminPwError(true); }
  };
  const saveVideoUrl = (modId, url) => {
    setVideoUrls(p => ({ ...p, [modId]: url.trim() }));
    setAdminVideoEditing(null); setAdminVideoInput("");
  };
  const getYTEmbed = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    const loom = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loom) return `https://www.loom.com/embed/${loom[1]}`;
    return url;
  };

  // ── Search ──
  const runSearch = (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); return; }
    const lower = q.toLowerCase();
    const results = ALL_MODS.filter(m => {
      const s = [m.title, m.summary, m.tag, ...(m.keyPoints||[])].join(" ").toLowerCase();
      return s.includes(lower);
    }).slice(0, 15);
    setSearchResults(results);
  };
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  useEffect(() => {
    if (searchOpen && searchInputRef.current) setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [searchOpen]);

  // ── Sync ──
  const buildSyncCode = () => {
    try {
      const payload = { completed, notes, quizPassed, studentName, onboardingDone, onboardingResult, level, videoUrls, lbName, affiliateCode, streakData, exported: new Date().toISOString(), version: "v5" };
      setSyncCode(btoa(JSON.stringify(payload)));
    } catch(e) { setSyncCode("ERROR"); }
  };
  const importSyncCode = () => {
    setSyncImportError(""); setSyncImportSuccess(false);
    try {
      const d = JSON.parse(atob(syncImportInput.trim()));
      if (d.version !== "v5") throw new Error("Wrong version");
      if (d.completed) setCompleted(d.completed);
      if (d.notes) setNotes(d.notes);
      if (d.quizPassed) setQuizPassed(d.quizPassed);
      if (d.studentName) setStudentName(d.studentName);
      if (d.onboardingDone) { setOnboardingDone(true); setOnboardingResult(d.onboardingResult); }
      if (d.level) setLevel(d.level);
      if (d.videoUrls) setVideoUrls(d.videoUrls);
      if (d.lbName) setLbName(d.lbName);
      if (d.affiliateCode) setAffiliateCode(d.affiliateCode);
      setSyncImportSuccess(true); setSyncImportInput("");
    } catch(e) { setSyncImportError("Invalid sync code."); }
  };

  // ── Threads ──
  const saveThreads = (updated) => {
    setThreads(updated);
    try { localStorage.setItem("flexx_cfd_v5_threads", JSON.stringify(updated)); } catch(e) {}
  };
  const postThread = (moduleId) => {
    if (!threadInput.trim()) return;
    const author = threadAuthor.trim() || "Anonymous";
    const entry = { id: Date.now(), author, text: threadInput.trim(), ts: new Date().toLocaleString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }), upvotes: 0 };
    saveThreads({ ...threads, [moduleId]: [entry, ...(threads[moduleId] || [])] });
    setThreadInput("");
  };
  const upvoteThread = (moduleId, entryId) => {
    const key = `upvoted_${moduleId}_${entryId}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    saveThreads({ ...threads, [moduleId]: (threads[moduleId] || []).map(e => e.id === entryId ? {...e, upvotes: e.upvotes+1} : e).sort((a,b) => b.upvotes - a.upvotes) });
  };


  useEffect(() => {
    const t = setInterval(() => setPulse(p => !p), 2000);
    return () => clearInterval(t);
  }, []);

  const currentLevel = COURSE.levels.find(l => l.id === level);
  const currentPhase = currentLevel?.phases[phaseIdx];

  const totalMods = ALL_MODS.length;
  const completedCount = Object.values(completed).filter(Boolean).length;
  const progressPct = Math.round((completedCount / totalMods) * 100);

  const phaseProgress = ph => {
    const done = ph.modules.filter(m => completed[m.id]).length;
    return { done, total: ph.modules.length, pct: Math.round((done / ph.modules.length) * 100) };
  };

  const openLesson = (mod, pIdx) => {
    setActiveLesson(mod);
    setPhaseIdx(pIdx);
    setNoteInput(notes[mod.id] || "");
    setExpandedSection(null);
    setView("lesson");
    window.scrollTo(0, 0);
  };

  const toggleDone = id => setCompleted(p => ({ ...p, [id]: !p[id] }));
  const saveNote = () => activeLesson && setNotes(p => ({ ...p, [activeLesson.id]: noteInput }));

  const navNext = () => {
    const cur = ALL_MODS.findIndex(m => m.id === activeLesson?.id);
    const next = ALL_MODS[cur + 1];
    if (next) {
      const lvl = COURSE.levels.find(l => l.id === next.levelId);
      const pi = lvl?.phases.findIndex(p => p.id === next.phaseId) ?? 0;
      openLesson(next, pi);
    }
  };

  const navPrev = () => {
    const cur = ALL_MODS.findIndex(m => m.id === activeLesson?.id);
    const prev = ALL_MODS[cur - 1];
    if (prev) {
      const lvl = COURSE.levels.find(l => l.id === prev.levelId);
      const pi = lvl?.phases.findIndex(p => p.id === prev.phaseId) ?? 0;
      openLesson(prev, pi);
    }
  };

  // Glossary data
  const GLOSSARY = [
    { term: "CFD", def: "Contract for Difference — a financial derivative where you profit from price movement without owning the underlying asset." },
    { term: "Long", def: "Buying a CFD expecting price to rise. Profit = Close Price − Entry Price × Contracts." },
    { term: "Short", def: "Selling a CFD expecting price to fall. Profit = Entry Price − Close Price × Contracts." },
    { term: "Margin", def: "The deposit required to open a leveraged CFD position. Typically 1%–20% of full position value." },
    { term: "Leverage", def: "The ratio of position value to margin. 1:10 means $1,000 margin controls $10,000 position." },
    { term: "Margin Call", def: "When account equity falls below the broker's minimum margin requirement — positions automatically closed." },
    { term: "Overnight Financing", def: "Daily interest charged on CFD positions held past market close. Also called 'swap' or 'rollover'." },
    { term: "Spread", def: "The difference between bid (sell) and ask (buy) price. Your transaction cost per trade." },
    { term: "Gap Risk", def: "When price jumps over your stop loss due to market closing/reopening. Creates unplanned slippage." },
    { term: "Slippage", def: "When your order executes at a different price than your stop/limit due to fast market conditions." },
    { term: "ECN Broker", def: "Electronic Communication Network broker — passes orders to real market. Better spreads, small commission." },
    { term: "Market Maker", def: "Broker that takes the opposite side of your trade. Potential conflict of interest." },
    { term: "BOS", def: "Break of Structure — price breaks a prior swing high (uptrend) or swing low (downtrend). Confirms trend continuation." },
    { term: "CHoCH", def: "Change of Character — first sign a trend may be reversing. Price breaks a structural level in the opposite direction." },
    { term: "Order Block", def: "The last opposing candle before a strong institutional move. Price often returns to this zone." },
    { term: "FVG", def: "Fair Value Gap — 3-candle pattern where price moves so fast it leaves a gap. Market tends to fill these gaps." },
    { term: "GSLO", def: "Guaranteed Stop-Loss Order — your stop executes at your exact level even through gaps. Usually costs extra." },
    { term: "DXY", def: "US Dollar Index — measures USD strength vs a basket of major currencies. Key driver for Gold and commodities." },
    { term: "Correlation", def: "How closely two assets move together. Positive = same direction. Negative = opposite directions." },
    { term: "Hedge", def: "An offsetting position that reduces risk. Example: Short index CFD while holding long stock portfolio." },
    { term: "Sector Rotation", def: "Movement of institutional capital between market sectors based on economic cycle phase." },
    { term: "Drawdown", def: "Peak-to-trough decline in account equity. Maximum drawdown = worst loss from a peak in history." },
    { term: "Prop Trading", def: "Trading a firm's capital instead of your own. Firms like FTMO provide capital for a share of profits." },
    { term: "Triple Swap", def: "On Wednesday night, brokers charge 3x overnight financing to account for Saturday/Sunday interest." },
  ].filter(g => g.term.toLowerCase().includes(glossaryFilter.toLowerCase()) || g.def.toLowerCase().includes(glossaryFilter.toLowerCase()));

  // ── SageMaster Product Modal ──
  const SageMasterModal = () => {
    if (!sageMasterOpen) return null;
    const prod = sageMasterProduct ? SAGEMASTER_PRODUCTS.find(p => p.id === sageMasterProduct) : null;
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.95)", zIndex:280, overflow:"auto", padding:"20px 16px" }}>
        <div style={{ maxWidth:700, margin:"0 auto" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:8, color:B.gold, letterSpacing:4, fontFamily:"monospace" }}>⚡ FLEXX × SAGEMASTER PRODUCT SUITE</div>
            <button onClick={() => { setSageMasterOpen(false); setSageMasterProduct(null); }} style={{ padding:"6px 12px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, cursor:"pointer", fontSize:11, fontFamily:"monospace" }}>✕ CLOSE</button>
          </div>

          {!prod ? (
            // Product grid
            <>
              <div style={{ padding:"18px 20px", marginBottom:16, background:B.blackCard, border:`1px solid ${B.gold+"40"}`, borderTop:`3px solid ${B.gold}` }}>
                <div style={{ fontSize:16, fontWeight:700, color:B.white, marginBottom:6 }}>SageMaster AI Trading Suite</div>
                <div style={{ fontSize:12, color:B.whiteGray, lineHeight:1.7, marginBottom:12 }}>
                  SageMaster is the AI trading platform built for the FLEXX system. Each tool in the suite maps directly to a step in your FLEXX trading process — from scanning to execution.
                </div>
                <a href="https://sagemaster.com" target="_blank" rel="noopener noreferrer" style={{ display:"inline-block", padding:"8px 18px", background:`linear-gradient(135deg,${B.gold},${B.goldDark})`, color:B.black, fontSize:9, letterSpacing:3, fontFamily:"monospace", fontWeight:700 }}>
                  VISIT SAGEMASTER.COM →
                </a>
              </div>
              <div style={{ display:"grid", gap:8, gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))" }}>
                {SAGEMASTER_PRODUCTS.map(p => (
                  <div key={p.id} className="hov-row" onClick={() => setSageMasterProduct(p.id)} style={{ padding:"14px 16px", background:B.blackCard, border:`1px solid ${B.blackBorder}`, borderLeft:`3px solid ${p.color}`, cursor:"pointer" }}>
                    <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                      <div style={{ fontSize:22, flexShrink:0 }}>{p.icon}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", gap:8, alignItems:"baseline", flexWrap:"wrap", marginBottom:4 }}>
                          <span style={{ fontSize:14, fontWeight:700, color:B.white }}>{p.name}</span>
                          <span style={{ fontSize:8, padding:"2px 7px", color:p.color, border:`1px solid ${p.color+"40"}`, fontFamily:"monospace", letterSpacing:1 }}>{p.tag}</span>
                        </div>
                        <div style={{ fontSize:11, color:B.whiteGray, lineHeight:1.5 }}>{p.tagline}</div>
                      </div>
                      <span style={{ color:B.gold, fontSize:14, flexShrink:0 }}>›</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            // Single product detail
            <div style={{ background:B.blackCard, border:`1px solid ${prod.color+"40"}`, borderTop:`3px solid ${prod.color}` }}>
              <div style={{ padding:"22px 24px" }}>
                <button onClick={() => setSageMasterProduct(null)} style={{ background:"transparent", border:"none", color:B.whiteMuted, cursor:"pointer", fontFamily:"monospace", fontSize:11, marginBottom:16 }}>← ALL TOOLS</button>
                <div style={{ fontSize:32, marginBottom:8 }}>{prod.icon}</div>
                <div style={{ fontSize:8, color:prod.color, letterSpacing:3, marginBottom:6, fontFamily:"monospace" }}>{prod.tag}</div>
                <div style={{ fontSize:22, fontWeight:700, color:B.white, marginBottom:8 }}>{prod.name}</div>
                <div style={{ fontSize:14, color:B.whiteGray, fontStyle:"italic", lineHeight:1.7, marginBottom:16 }}>{prod.tagline}</div>
                <div style={{ padding:"14px 16px", background:`${prod.color}10`, border:`1px solid ${prod.color+"30"}`, marginBottom:16 }}>
                  <div style={{ fontSize:8, color:prod.color, letterSpacing:2, marginBottom:8, fontFamily:"monospace" }}>◆ WHAT IT DOES</div>
                  <div style={{ fontSize:13, color:B.whiteGray, lineHeight:1.75 }}>{prod.desc}</div>
                </div>
                <div style={{ padding:"14px 16px", background:B.goldFaint, border:`1px solid ${B.gold+"30"}`, borderLeft:`3px solid ${B.gold}`, marginBottom:20 }}>
                  <div style={{ fontSize:8, color:B.gold, letterSpacing:2, marginBottom:8, fontFamily:"monospace" }}>⚡ FLEXX USE CASE — HOW TO USE THIS IN YOUR TRADING</div>
                  <div style={{ fontSize:13, color:B.whiteGray, lineHeight:1.75 }}>{prod.useCase}</div>
                </div>
                <a href={prod.url} target="_blank" rel="noopener noreferrer" style={{ display:"inline-block", padding:"10px 22px", background:`linear-gradient(135deg,${B.gold},${B.goldDark})`, color:B.black, fontSize:10, letterSpacing:3, fontFamily:"monospace", fontWeight:700 }}>
                  OPEN {prod.name.toUpperCase()} →
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Leaderboard Modal ──
  const LeaderboardModal = () => {
    if (!showLeaderboard) return null;
    const myRank = leaderboardData.findIndex(e => e.name === lbName) + 1;
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.95)", zIndex:280, overflow:"auto", padding:"20px 16px" }}>
        <div style={{ maxWidth:600, margin:"0 auto" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:8, color:B.gold, letterSpacing:4, fontFamily:"monospace" }}>🏆 FLEXX ACADEMY LEADERBOARD</div>
            <button onClick={() => setShowLeaderboard(false)} style={{ padding:"6px 12px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, cursor:"pointer", fontSize:11, fontFamily:"monospace" }}>✕ CLOSE</button>
          </div>

          {/* Submit your score */}
          {!lbSubmitted ? (
            <div style={{ padding:"16px 18px", marginBottom:16, background:B.blackCard, border:`1px solid ${B.gold+"40"}`, borderTop:`3px solid ${B.gold}` }}>
              <div style={{ fontSize:8, color:B.gold, letterSpacing:3, marginBottom:8, fontFamily:"monospace" }}>ADD YOUR SCORE TO THE BOARD</div>
              <div style={{ fontSize:11, color:B.whiteGray, marginBottom:12 }}>Enter your name to add your current progress ({progress}% · {streakData.streak} day streak · {Object.values(quizPassed).filter(Boolean).length} quizzes) to the leaderboard.</div>
              <div style={{ display:"flex", gap:8 }}>
                <input value={lbName} onChange={e => setLbName(e.target.value)} placeholder="Your name or nickname" style={{ flex:1, padding:"8px 12px", background:B.blackPanel, border:`1px solid ${B.gold+"40"}`, color:B.white, fontSize:12, fontFamily:"Georgia,serif", outline:"none" }} onKeyDown={e => e.key === "Enter" && submitToLeaderboard()} />
                <button className="hov" onClick={submitToLeaderboard} style={{ padding:"8px 16px", background:`linear-gradient(135deg,${B.gold},${B.goldDark})`, color:B.black, fontSize:9, letterSpacing:2, fontFamily:"monospace", fontWeight:700, border:"none", cursor:"pointer" }}>
                  POST →
                </button>
              </div>
            </div>
          ) : (
            <div style={{ padding:"12px 16px", marginBottom:16, background:B.goldFaint, border:`1px solid ${B.gold+"30"}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:11, color:B.gold, fontFamily:"monospace" }}>
                {myRank > 0 ? `You're ranked #${myRank} — keep going!` : "Your score is posted!"}
              </div>
              <button className="hov" onClick={() => { setLbSubmitted(false); submitToLeaderboard(); }} style={{ padding:"5px 12px", background:"transparent", color:B.gold, border:`1px solid ${B.gold+"50"}`, fontSize:9, letterSpacing:1, fontFamily:"monospace", cursor:"pointer" }}>
                UPDATE SCORE
              </button>
            </div>
          )}

          {/* Board */}
          {leaderboardData.length === 0 ? (
            <div style={{ padding:"30px", textAlign:"center", color:B.whiteMuted, fontFamily:"monospace", fontSize:11 }}>
              No entries yet. Be the first to post your score!
            </div>
          ) : (
            <div style={{ display:"grid", gap:5 }}>
              {leaderboardData.map((entry, i) => {
                const isMe = entry.name === lbName;
                const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i+1}.`;
                return (
                  <div key={i} style={{ padding:"12px 16px", background:isMe ? B.goldFaint : B.blackCard, border:`1px solid ${isMe ? B.gold+"40" : B.blackBorder}`, display:"flex", gap:12, alignItems:"center" }}>
                    <div style={{ width:32, textAlign:"center", fontSize:16, flexShrink:0 }}>{medal}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:isMe ? B.gold : B.white, marginBottom:2 }}>{entry.name}{isMe ? " (you)" : ""}</div>
                      <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>{entry.track} · {entry.date}</div>
                    </div>
                    <div style={{ display:"flex", gap:12, flexShrink:0, textAlign:"right" }}>
                      <div>
                        <div style={{ fontSize:16, fontWeight:700, color:B.gold, fontFamily:"monospace" }}>{entry.pct}%</div>
                        <div style={{ fontSize:8, color:B.whiteMuted, fontFamily:"monospace" }}>{entry.completed}/{entry.total}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:B.goldLight, fontFamily:"monospace" }}>🔥{entry.streak}</div>
                        <div style={{ fontSize:8, color:B.whiteMuted, fontFamily:"monospace" }}>STREAK</div>
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:B.green, fontFamily:"monospace" }}>🏆{entry.quizzes}</div>
                        <div style={{ fontSize:8, color:B.whiteMuted, fontFamily:"monospace" }}>QUIZZES</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop:12, fontSize:9, color:B.whiteMuted, fontFamily:"monospace", textAlign:"center" }}>
            Leaderboard stored locally in your browser. Share your rank to recruit new students to BE/FLEXX.
          </div>
        </div>
      </div>
    );
  };

  // ── Admin Login Modal ──

  // ── Admin Login Modal (inline to prevent focus loss) ──

  // ── Admin View ──
  const AdminView = () => {
    if (view !== "admin" || !adminMode) return null;
    return (
      <div style={{ maxWidth:900, margin:"0 auto", padding:"22px 16px" }}>
        <div style={{ padding:"16px 18px", marginBottom:20, background:B.blackCard, border:`1px solid ${B.gold+"50"}`, borderTop:`3px solid ${B.gold}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
            <div>
              <div style={{ fontSize:8, color:B.gold, letterSpacing:4, marginBottom:4, fontFamily:"monospace" }}>🎬 ADMIN MEDIA DASHBOARD</div>
              <div style={{ fontSize:16, fontWeight:700, color:B.white }}>Lesson Video Management</div>
              <div style={{ fontSize:11, color:B.whiteGray, marginTop:4 }}>Paste YouTube or Loom URLs per lesson. Students will see an embedded player at the top of each lesson.</div>
            </div>
            <button className="hov" onClick={() => { setAdminMode(false); setView("map"); }} style={{ padding:"7px 14px", background:"transparent", color:B.red, border:`1px solid ${B.red+"50"}`, fontSize:9, letterSpacing:2, fontFamily:"monospace", cursor:"pointer" }}>
              EXIT ADMIN
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
          {[
            { label:"TOTAL LESSONS", val:ALL_MODS.length, col:B.gold },
            { label:"VIDEOS ADDED", val:Object.values(videoUrls).filter(Boolean).length, col:B.green },
            { label:"LESSONS WITHOUT VIDEO", val:ALL_MODS.length - Object.values(videoUrls).filter(Boolean).length, col:B.red },
          ].map((s, i) => (
            <div key={i} style={{ padding:"10px 14px", background:B.blackCard, border:`1px solid ${B.blackBorder}`, borderTop:`2px solid ${s.col+"40"}`, flex:1, minWidth:120 }}>
              <div style={{ fontSize:8, color:B.whiteMuted, fontFamily:"monospace", letterSpacing:2, marginBottom:4 }}>{s.label}</div>
              <div style={{ fontSize:22, fontWeight:700, color:s.col, fontFamily:"monospace" }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Module list by track */}
        {COURSE.levels.map(lv => (
          <div key={lv.id} style={{ marginBottom:24 }}>
            <div style={{ fontSize:8, color:B.cfd, letterSpacing:3, marginBottom:10, fontFamily:"monospace", padding:"6px 0", borderBottom:`1px solid ${B.blackBorder}` }}>
              CFD MASTERY · {lv.label}
            </div>
            {lv.phases.map(ph => (
              <div key={ph.id} style={{ marginBottom:16 }}>
                <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace", marginBottom:8, paddingLeft:8 }}>
                  {ph.icon} {ph.label} · {ph.weeks}
                </div>
                <div style={{ display:"grid", gap:5 }}>
                  {ph.modules.map(mod => {
                    const hasVideo = !!(videoUrls[mod.id]);
                    const isEditing = adminVideoEditing === mod.id;
                    return (
                      <div key={mod.id} style={{ padding:"12px 14px", background:B.blackCard, border:`1px solid ${hasVideo ? B.green+"30" : B.blackBorder}` }}>
                        <div style={{ display:"flex", gap:10, alignItems:"flex-start", flexWrap:"wrap" }}>
                          <div style={{ flex:1, minWidth:200 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:B.white, marginBottom:2 }}>{mod.title}</div>
                            <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>{mod.id} · {mod.duration}</div>
                          </div>
                          <div style={{ flex:2, minWidth:250 }}>
                            {isEditing ? (
                              <div style={{ display:"flex", gap:6 }}>
                                <input value={adminVideoInput} onChange={e => setAdminVideoInput(e.target.value)} onKeyDown={e => e.key === "Enter" && saveVideoUrl(mod.id, adminVideoInput)} placeholder="https://youtube.com/watch?v=... or loom.com/share/..." style={{ flex:1, padding:"6px 10px", background:B.blackPanel, border:`1px solid ${B.gold+"50"}`, color:B.white, fontSize:11, fontFamily:"monospace", outline:"none" }} autoFocus />
                                <button onClick={() => saveVideoUrl(mod.id, adminVideoInput)} style={{ padding:"6px 12px", background:B.gold, color:B.black, border:"none", cursor:"pointer", fontFamily:"monospace", fontSize:9, fontWeight:700 }}>SAVE</button>
                                <button onClick={() => { setAdminVideoEditing(null); setAdminVideoInput(""); }} style={{ padding:"6px 10px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, cursor:"pointer", fontFamily:"monospace", fontSize:9 }}>✕</button>
                              </div>
                            ) : (
                              <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                                {hasVideo ? (
                                  <div style={{ fontSize:10, color:B.green, fontFamily:"monospace", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                    ✓ {videoUrls[mod.id].slice(0, 55)}{videoUrls[mod.id].length > 55 ? "..." : ""}
                                  </div>
                                ) : (
                                  <div style={{ fontSize:10, color:B.whiteMuted, fontFamily:"monospace", flex:1 }}>No video URL set</div>
                                )}
                                <button className="hov" onClick={() => { setAdminVideoEditing(mod.id); setAdminVideoInput(videoUrls[mod.id] || ""); }} style={{ padding:"4px 10px", background:"transparent", color:B.cfd, border:`1px solid ${B.cfd+"50"}`, fontSize:9, letterSpacing:1, fontFamily:"monospace", cursor:"pointer", flexShrink:0 }}>
                                  {hasVideo ? "EDIT" : "+ ADD VIDEO"}
                                </button>
                                {hasVideo && (
                                  <button className="hov" onClick={() => saveVideoUrl(mod.id, "")} style={{ padding:"4px 8px", background:"transparent", color:B.red, border:`1px solid ${B.red+"40"}`, fontSize:9, fontFamily:"monospace", cursor:"pointer", flexShrink:0 }}>
                                    REMOVE
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // ── FEATURE 7: Global Search Modal ──
  const SearchModal = () => {
    if (!searchOpen) return null;
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:400, display:"flex", flexDirection:"column", padding:"60px 16px 20px" }} onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}>
        <div style={{ maxWidth:680, width:"100%", margin:"0 auto" }}>
          {/* Search input */}
          <div style={{ display:"flex", gap:10, marginBottom:12, background:B.blackCard, border:`1px solid ${B.gold+"60"}`, borderTop:`3px solid ${B.gold}`, padding:"14px 16px" }}>
            <span style={{ color:B.gold, fontSize:18, flexShrink:0 }}>🔍</span>
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={e => runSearch(e.target.value)}
              placeholder="Search lessons, concepts, modules, strategies..."
              style={{ flex:1, background:"transparent", border:"none", color:B.white, fontSize:15, fontFamily:"Georgia,serif", outline:"none" }}
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(""); setSearchResults([]); }} style={{ background:"transparent", border:"none", color:B.whiteMuted, cursor:"pointer", fontSize:14 }}>✕</button>
            )}
            <button onClick={() => setSearchOpen(false)} style={{ background:"transparent", border:`1px solid ${B.blackBorder}`, color:B.whiteMuted, cursor:"pointer", fontSize:9, fontFamily:"monospace", padding:"4px 10px" }}>ESC</button>
          </div>

          {/* Hints */}
          {!searchQuery && (
            <div style={{ padding:"16px 18px", background:B.blackCard, border:`1px solid ${B.blackBorder}` }}>
              <div style={{ fontSize:8, color:B.gold, letterSpacing:3, marginBottom:12, fontFamily:"monospace" }}>SEARCH ACROSS ALL TRACKS, LEVELS & LESSONS</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {["candlestick", "support resistance", "risk management", "smart money", "SageMaster", "EMA", "fibonacci", "journal"].map(hint => (
                  <button key={hint} className="hov" onClick={() => runSearch(hint)} style={{ padding:"5px 12px", background:"transparent", border:`1px solid ${B.blackBorder}`, color:B.whiteGray, fontSize:11, fontFamily:"monospace", cursor:"pointer" }}>
                    {hint}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          {searchQuery && (
            <div style={{ maxHeight:"55vh", overflowY:"auto" }}>
              {searchResults.length === 0 ? (
                <div style={{ padding:"24px 18px", background:B.blackCard, border:`1px solid ${B.blackBorder}`, textAlign:"center", color:B.whiteMuted, fontSize:12, fontFamily:"monospace" }}>
                  No results for "{searchQuery}" — try different keywords
                </div>
              ) : (
                <>
                  <div style={{ fontSize:8, color:B.whiteMuted, fontFamily:"monospace", marginBottom:6, padding:"0 4px" }}>
                    {searchResults.length} RESULT{searchResults.length !== 1 ? "S" : ""} FOR "{searchQuery.toUpperCase()}"
                  </div>
                  <div style={{ display:"grid", gap:5 }}>
                    {searchResults.map((mod, i) => {
                      const hilite = (str) => {
                        if (!str) return str;
                        const idx = str.toLowerCase().indexOf(searchQuery.toLowerCase());
                        if (idx === -1) return str;
                        return <>{str.slice(0, idx)}<mark style={{ background:B.gold+"40", color:B.gold, padding:"0 2px" }}>{str.slice(idx, idx + searchQuery.length)}</mark>{str.slice(idx + searchQuery.length)}</>;
                      };
                      return (
                        <div key={i} className="hov-row" onClick={() => {
                          setTrack(mod.trackId);
                          setLevel(mod.levelId);
                          setPhaseIdx(mod.phaseIdx);
                          openLesson(mod, mod.phaseIdx);
                          setSearchOpen(false);
                          setSearchQuery("");
                          setSearchResults([]);
                        }} style={{ padding:"12px 16px", background:B.blackCard, border:`1px solid ${B.blackBorder}`, cursor:"pointer" }}>
                          <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:3, alignItems:"baseline" }}>
                                <span style={{ fontSize:14, fontWeight:700, color:B.white }}>{hilite(mod.title)}</span>
                                <span style={{ fontSize:8, padding:"2px 7px", color:mod.trackAccent, border:`1px solid ${mod.trackAccent+"40"}`, fontFamily:"monospace" }}>{mod.tag}</span>
                                {completed[mod.id] && <span style={{ fontSize:8, padding:"2px 7px", color:B.green, border:`1px solid ${B.green+"40"}`, fontFamily:"monospace" }}>✓ DONE</span>}
                              </div>
                              <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace", marginBottom:4 }}>
                                {mod.trackLabel} · {mod.levelLabel} · {mod.phaseLabel}
                              </div>
                              <div style={{ fontSize:11, color:B.whiteGray, lineHeight:1.5 }}>{hilite(mod.summary?.slice(0,100))}...</div>
                            </div>
                            <span style={{ color:mod.trackAccent, fontSize:16, flexShrink:0 }}>›</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── FEATURE 8: Sync Modal ──
  const SyncModal = () => {
    if (!syncOpen) return null;
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.95)", zIndex:380, overflow:"auto", padding:"20px 16px" }}>
        <div style={{ maxWidth:580, margin:"0 auto" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:8, color:B.gold, letterSpacing:4, fontFamily:"monospace" }}>📱 CROSS-DEVICE SYNC</div>
            <button onClick={() => { setSyncOpen(false); setSyncCode(""); setSyncImportInput(""); setSyncImportError(""); setSyncImportSuccess(false); }} style={{ padding:"6px 12px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, cursor:"pointer", fontSize:11, fontFamily:"monospace" }}>✕ CLOSE</button>
          </div>

          {/* EXPORT */}
          <div style={{ padding:"18px 20px", marginBottom:12, background:B.blackCard, border:`1px solid ${B.gold+"40"}`, borderTop:`3px solid ${B.gold}` }}>
            <div style={{ fontSize:8, color:B.gold, letterSpacing:3, marginBottom:8, fontFamily:"monospace" }}>📤 EXPORT — COPY YOUR PROGRESS CODE</div>
            <div style={{ fontSize:12, color:B.whiteGray, marginBottom:14, lineHeight:1.65 }}>
              Generate a sync code that captures your entire progress — completed lessons, notes, quiz passes, streak, settings. Copy it and paste it on any other device to continue right where you left off.
            </div>
            <button className="hov" onClick={buildSyncCode} style={{ padding:"10px 20px", background:`linear-gradient(135deg,${B.gold},${B.goldDark})`, color:B.black, fontSize:10, letterSpacing:3, fontFamily:"monospace", fontWeight:700, border:"none", cursor:"pointer", marginBottom: syncCode ? 12 : 0 }}>
              GENERATE SYNC CODE →
            </button>
            {syncCode && (
              <>
                <div style={{ padding:"10px 12px", background:B.blackPanel, border:`1px solid ${B.blackBorder}`, fontFamily:"monospace", fontSize:10, color:B.whiteGray, wordBreak:"break-all", lineHeight:1.6, marginBottom:8 }}>
                  {syncCode.slice(0, 120)}...
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button className="hov" onClick={() => { navigator.clipboard?.writeText(syncCode).catch(() => {}); }} style={{ flex:1, padding:"8px", background:"transparent", color:B.green, border:`1px solid ${B.green+"50"}`, fontSize:9, letterSpacing:2, fontFamily:"monospace", cursor:"pointer" }}>
                    📋 COPY FULL CODE
                  </button>
                  <button className="hov" onClick={() => {
                    const blob = new Blob([syncCode], { type:"text/plain" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = "flexx_sync.txt"; a.click();
                    URL.revokeObjectURL(url);
                  }} style={{ flex:1, padding:"8px", background:"transparent", color:B.gold, border:`1px solid ${B.gold+"50"}`, fontSize:9, letterSpacing:2, fontFamily:"monospace", cursor:"pointer" }}>
                    ⬇ DOWNLOAD AS FILE
                  </button>
                </div>
                <div style={{ marginTop:8, fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>
                  Exported: {progress}% complete · {completedCount} lessons · 🔥{streakData.streak} streak
                </div>
              </>
            )}
          </div>

          {/* IMPORT */}
          <div style={{ padding:"18px 20px", background:B.blackCard, border:`1px solid ${B.blackBorder}` }}>
            <div style={{ fontSize:8, color:B.cfd, letterSpacing:3, marginBottom:8, fontFamily:"monospace" }}>📥 IMPORT — PASTE YOUR SYNC CODE</div>
            <div style={{ fontSize:12, color:B.whiteGray, marginBottom:12 }}>
              Paste a sync code from another device to restore your progress here. This will overwrite your current progress on this device.
            </div>
            <textarea
              value={syncImportInput}
              onChange={e => { setSyncImportInput(e.target.value); setSyncImportError(""); setSyncImportSuccess(false); }}
              placeholder="Paste your sync code here..."
              style={{ width:"100%", minHeight:80, padding:10, background:B.blackPanel, border:`1px solid ${syncImportError ? B.red+"60" : B.blackBorder}`, color:B.whiteGray, fontSize:11, fontFamily:"monospace", outline:"none", resize:"vertical", lineHeight:1.5, marginBottom:8 }}
            />
            {syncImportError && <div style={{ fontSize:10, color:B.red, fontFamily:"monospace", marginBottom:8 }}>{syncImportError}</div>}
            {syncImportSuccess && <div style={{ fontSize:10, color:B.green, fontFamily:"monospace", marginBottom:8 }}>✓ Progress imported successfully! Your data has been restored.</div>}
            <button className="hov" onClick={importSyncCode} disabled={!syncImportInput.trim()} style={{ padding:"10px 20px", background:syncImportInput.trim() ? `linear-gradient(135deg,${B.cfd},${B.goldDark})` : B.blackBorder, color:syncImportInput.trim() ? B.black : B.whiteMuted, fontSize:10, letterSpacing:3, fontFamily:"monospace", fontWeight:700, border:"none", cursor:syncImportInput.trim() ? "pointer" : "default" }}>
              IMPORT & RESTORE →
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── FEATURE 9: Community Thread Component (used inside lesson view) ──
  const LessonThread = ({ moduleId }) => {
    const entries = (threads[moduleId] || []);
    return (
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:8, color:B.whiteGray, letterSpacing:3, marginBottom:10, fontFamily:"monospace" }}>💬 STUDENT DISCUSSION ({entries.length})</div>

        {/* Post form */}
        <div style={{ padding:"14px 16px", background:B.blackCard, border:`1px solid ${B.blackBorder}`, marginBottom:10 }}>
          <div style={{ display:"flex", gap:8, marginBottom:8 }}>
            <input
              value={threadAuthor}
              onChange={e => setThreadAuthor(e.target.value)}
              placeholder="Your name (optional)"
              style={{ flex:"0 0 140px", padding:"7px 10px", background:B.blackPanel, border:`1px solid ${B.blackBorder}`, color:B.whiteGray, fontSize:11, fontFamily:"monospace", outline:"none" }}
            />
            <input
              value={threadInput}
              onChange={e => setThreadInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && postThread(moduleId)}
              placeholder="Share a question, insight, or tip from this lesson..."
              style={{ flex:1, padding:"7px 10px", background:B.blackPanel, border:`1px solid ${B.blackBorder}`, color:B.whiteGray, fontSize:12, fontFamily:"Georgia,serif", outline:"none" }}
            />
            <button className="hov" onClick={() => postThread(moduleId)} style={{ padding:"7px 14px", background:`linear-gradient(135deg,${B.cfd},${B.goldDark})`, color:B.black, fontSize:9, letterSpacing:2, fontFamily:"monospace", fontWeight:700, border:"none", cursor:"pointer", flexShrink:0 }}>
              POST
            </button>
          </div>
          <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>Press Enter to post · Upvote the most helpful comments</div>
        </div>

        {/* Thread entries */}
        {entries.length === 0 ? (
          <div style={{ padding:"16px", textAlign:"center", color:B.whiteMuted, fontSize:11, fontFamily:"monospace", border:`1px solid ${B.blackBorder}` }}>
            No comments yet — be the first to share an insight from this lesson.
          </div>
        ) : (
          <div style={{ display:"grid", gap:6 }}>
            {entries.map((entry) => {
              const alreadyVoted = (() => { try { return !!localStorage.getItem(`upvoted_${moduleId}_${entry.id}`); } catch(e) { return false; } })();
              return (
                <div key={entry.id} style={{ padding:"12px 14px", background:B.blackCard, border:`1px solid ${B.blackBorder}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, flexWrap:"wrap", gap:4 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:B.cfd }}>{entry.author}</span>
                    <span style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>{entry.ts}</span>
                  </div>
                  <div style={{ fontSize:13, color:B.whiteGray, lineHeight:1.65, marginBottom:8 }}>{entry.text}</div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <button className="hov" onClick={() => upvoteThread(moduleId, entry.id)} style={{ padding:"3px 10px", background:alreadyVoted ? `${B.cfd}20` : "transparent", color:alreadyVoted ? B.cfd : B.whiteMuted, border:`1px solid ${alreadyVoted ? B.cfd+"50" : B.blackBorder}`, fontSize:9, fontFamily:"monospace", cursor:"pointer" }}>
                      ▲ {entry.upvotes} {entry.upvotes === 1 ? "upvote" : "upvotes"}
                    </button>
                    {(entry.author === (threadAuthor || "Anonymous")) && (
                      <button className="hov" onClick={() => deleteThread(moduleId, entry.id)} style={{ padding:"3px 8px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, fontSize:9, fontFamily:"monospace", cursor:"pointer" }}>
                        delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Onboarding Quiz Modal ──
  const OnboardingQuiz = () => {
    if (onboardingDone) return null;
    const q = ONBOARDING_QUIZ[onboardingStep];
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.97)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px 16px" }}>
        <div style={{ maxWidth:560, width:"100%", background:B.blackCard, border:`1px solid ${B.gold+"50"}`, borderTop:`3px solid ${B.gold}` }}>
          {/* Header */}
          <div style={{ padding:"24px 26px 0" }}>
            <img src={FLEXX_LOGO} alt="FLEXX" style={{ height:32, marginBottom:18, opacity:0.9 }} />
            <div style={{ fontSize:8, color:B.gold, letterSpacing:4, marginBottom:10, fontFamily:"monospace" }}>STUDENT PLACEMENT · QUESTION {onboardingStep+1} OF {ONBOARDING_QUIZ.length}</div>
            {/* Progress bar */}
            <div style={{ height:2, background:B.blackBorder, marginBottom:20 }}>
              <div style={{ width:`${((onboardingStep)/ONBOARDING_QUIZ.length)*100}%`, height:"100%", background:B.gold, transition:"width 0.4s" }} />
            </div>
            <div style={{ fontSize:18, fontWeight:700, color:B.white, lineHeight:1.5, marginBottom:22 }}>{q.question}</div>
          </div>
          {/* Options */}
          <div style={{ padding:"0 26px 28px", display:"grid", gap:8 }}>
            {q.options.map((opt, i) => (
              <button key={i} className="hov" onClick={() => handleOnboardingAnswer(q.id, opt.value)} style={{ padding:"13px 16px", background:"transparent", border:`1px solid ${B.blackBorder}`, color:B.whiteGray, fontSize:13, textAlign:"left", cursor:"pointer", transition:"all 0.2s", fontFamily:"Georgia,serif", lineHeight:1.5 }}>
                <span style={{ color:B.gold, fontFamily:"monospace", fontSize:10, marginRight:10 }}>{String.fromCharCode(65+i)}.</span>
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ padding:"0 26px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>FLEXX ACADEMY · TRACK PLACEMENT</div>
            {onboardingStep > 0 && (
              <button onClick={() => setOnboardingStep(s => s-1)} style={{ background:"transparent", border:"none", color:B.whiteMuted, fontSize:11, cursor:"pointer", fontFamily:"monospace" }}>← BACK</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Module Quiz Modal ──
  const QuizModal = () => {
    if (!activeQuiz) return null;
    const quiz = MODULE_QUIZZES[activeQuiz.moduleId];
    if (!quiz) return null;

    // Results screen
    if (quizResult) {
      return (
        <FlexxQuizResultPanel
          passed={quizResult.passed}
          score={quizResult.score}
          total={quizResult.total}
          moduleTitle={activeQuiz.moduleTitle}
          tierLabel="CFD Mastery"
          studentName={studentName}
          setStudentName={setStudentName}
          affiliateCode={affiliateCode}
          setAffiliateCode={setAffiliateCode}
          affiliatePlaceholder="BE Referral Code (optional — e.g. FLEXX123)"
          onGenerateCert={() => { generateCert(studentName, activeQuiz.moduleTitle, "CFD Mastery"); setActiveQuiz(null); setQuizResult(null); }}
          onTryAgain={() => { setQuizStep(0); setQuizAnswers({}); setQuizResult(null); }}
          onClose={() => { setActiveQuiz(null); setQuizResult(null); }}
        />
      );
    }

    // Active question
    const q = quiz.questions[quizStep];
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.95)", zIndex:250, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px 16px" }}>
        <div style={{ maxWidth:560, width:"100%", background:B.blackCard, border:`1px solid ${B.gold+"50"}`, borderTop:`3px solid ${B.gold}` }}>
          <div style={{ padding:"24px 26px 0" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:16 }}>
              <div style={{ fontSize:8, color:B.gold, letterSpacing:3, fontFamily:"monospace" }}>MODULE QUIZ · {quiz.moduleTitle.toUpperCase()}</div>
              <button onClick={() => setActiveQuiz(null)} style={{ background:"transparent", border:"none", color:B.whiteMuted, cursor:"pointer", fontFamily:"monospace", fontSize:11 }}>✕</button>
            </div>
            {/* Progress */}
            <div style={{ height:2, background:B.blackBorder, marginBottom:6 }}>
              <div style={{ width:`${(quizStep/quiz.questions.length)*100}%`, height:"100%", background:B.gold, transition:"width 0.3s" }} />
            </div>
            <div style={{ fontSize:9, color:B.whiteMuted, fontFamily:"monospace", marginBottom:18 }}>QUESTION {quizStep+1} OF {quiz.questions.length}</div>
            <div style={{ fontSize:16, fontWeight:700, color:B.white, lineHeight:1.6, marginBottom:22 }}>{q.q}</div>
          </div>
          <div style={{ padding:"0 26px 26px", display:"grid", gap:8 }}>
            {q.options.map((opt, i) => (
              <button key={i} className="hov" onClick={() => handleQuizAnswer(quizStep, i)} style={{ padding:"12px 16px", background:"transparent", border:`1px solid ${B.blackBorder}`, color:B.whiteGray, fontSize:13, textAlign:"left", cursor:"pointer", fontFamily:"Georgia,serif", lineHeight:1.4 }}>
                <span style={{ color:B.gold, fontFamily:"monospace", fontSize:10, marginRight:10 }}>{String.fromCharCode(65+i)}.</span>
                {opt}
              </button>
            ))}
          </div>
          <div style={{ padding:"0 26px 18px", fontSize:9, color:B.whiteMuted, fontFamily:"monospace" }}>Need 75% (3/4) to pass and earn your certificate</div>
        </div>
      </div>
    );
  };

  // ── Certificate Modal ──
  const CertModal = () => {
    if (!showCert) return null;
    const verifyCode = showCert.verifyCode || (Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4));
    const courseSubtitle = `${showCert.track || "FLEXX ACADEMY"} — a specialized component of the FLEXX ACADEMY Elite Trading Series`;
    const onDownloadPdf = () => openCertificatePrintView({
      studentName: showCert.name,
      courseName: showCert.moduleTitle,
      courseSubtitle,
      completionDate: showCert.date,
      verifyCode,
      tier: "intermediate",
    });
    return (
      <FlexxCertificate
        studentName={showCert.name}
        courseName={showCert.moduleTitle}
        courseSubtitle={courseSubtitle}
        completionDate={showCert.date}
        verifyCode={verifyCode}
        tier="intermediate"
        onClose={() => setShowCert(null)}
        onDownload={onDownloadPdf}
      />
    );
  };


  return (
    <div style={{ fontFamily: "'Georgia', serif", background: B.blackDeep, minHeight: "100vh", color: B.whiteOff }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: #2A2A2A; border-radius: 2px; }
        .mod-card:hover { background: rgba(0,212,170,0.03) !important; border-color: rgba(0,212,170,0.15) !important; }
        .res-card:hover { transform: translateX(4px); background: rgba(255,255,255,0.04) !important; }
        .hov { cursor: pointer; transition: all 0.2s; }
        .hov:hover { opacity: 0.8; }
        .expand-btn:hover { background: rgba(0,212,170,0.08) !important; }
        @keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: fadeIn 0.3s ease forwards; }
        .pulse { animation: blink 3s infinite; }
      `}</style>

      {/* ── v5 MODAL MOUNTS ── */}
      <OnboardingQuiz />
      {activeQuiz && QuizModal()}
      {showCert && <CertModal />}
      {sageMasterOpen && <SageMasterModal />}
      {showLeaderboard && <LeaderboardModal />}
      {showAdminLogin && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.97)", zIndex:290, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px 16px" }}>
          <div style={{ maxWidth:400, width:"100%", background:B.blackCard, border:`1px solid ${B.gold+"50"}`, borderTop:`3px solid ${B.gold}` }}>
            <div style={{ padding:"28px 26px" }}>
              <div style={{ fontSize:8, color:B.gold, letterSpacing:4, marginBottom:12, fontFamily:"monospace" }}>🔐 ADMIN ACCESS</div>
              <div style={{ fontSize:16, fontWeight:700, color:B.white, marginBottom:6 }}>Media Dashboard</div>
              <div style={{ fontSize:12, color:B.whiteGray, marginBottom:20 }}>Enter the admin password to manage video content for each lesson.</div>
              <input type="password" value={adminPwInput} onChange={e => { setAdminPwInput(e.target.value); setAdminPwError(false); }} onKeyDown={e => e.key === "Enter" && handleAdminLogin()} placeholder="Admin password" autoFocus style={{ width:"100%", padding:"10px 12px", background:B.blackPanel, border:`1px solid ${adminPwError ? B.red+"60" : B.gold+"40"}`, color:B.white, fontSize:13, fontFamily:"monospace", outline:"none", marginBottom:adminPwError ? 6 : 12 }} />
              {adminPwError && <div style={{ fontSize:10, color:B.red, fontFamily:"monospace", marginBottom:10 }}>Incorrect password</div>}
              <div style={{ display:"flex", gap:8 }}>
                <button className="hov" onClick={handleAdminLogin} style={{ flex:1, padding:"10px", background:`linear-gradient(135deg,${B.gold},${B.goldDark})`, color:B.black, fontSize:9, letterSpacing:3, fontFamily:"monospace", fontWeight:700, border:"none", cursor:"pointer" }}>ENTER →</button>
                <button className="hov" onClick={() => { setShowAdminLogin(false); setAdminPwInput(""); setAdminPwError(false); }} style={{ padding:"10px 16px", background:"transparent", color:B.whiteMuted, border:`1px solid ${B.blackBorder}`, fontSize:9, letterSpacing:2, fontFamily:"monospace", cursor:"pointer" }}>CANCEL</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {searchOpen && <SearchModal />}
      {syncOpen && <SyncModal />}
      {view === "admin" && adminMode && <AdminView />}


      {/* ── HEADER ── */}
      <div style={{ position: "sticky", top: 0, zIndex: 100, background: B.black, borderBottom: `1px solid ${B.blackBorder}` }}>
        <GoldBar />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", flexWrap: "wrap", gap: 10 }}>

          {/* Logo + Title */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src={FLEXX_LOGO} alt="FLEXX" style={{ height: 38, width: "auto", objectFit: "contain" }} />
            <div style={{ width: 1, height: 24, background: B.blackBorder }} />
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: B.cfd, letterSpacing: 2, fontFamily: "'Arial Black', sans-serif" }}>CFD</div>
                <Tag color={B.cfd} size={7}>CONTRACTS FOR DIFFERENCE</Tag>
                <Tag color={B.whiteMuted} size={7}>{COURSE.badge}</Tag>
              </div>
              <div style={{ fontSize: 8, color: B.whiteMuted, letterSpacing: 2, fontFamily: "monospace" }}>FLEXX ACADEMY · DUBAI 2026</div>
            </div>
          </div>

          {/* Center nav */}
          <div style={{ display: "flex", gap: 4 }}>
            {[
              { id: "map", label: "COURSE MAP" },
              { id: "glossary", label: "GLOSSARY" },
            ].map(v => (
              <button key={v.id} className="hov" onClick={() => setView(v.id)} style={{
                padding: "6px 14px", fontSize: 8, letterSpacing: 2, fontFamily: "monospace",
                background: view === v.id ? `linear-gradient(135deg, ${B.cfd}, ${B.cfdDark})` : "transparent",
                color: view === v.id ? B.black : B.whiteGray,
                border: `1px solid ${view === v.id ? B.cfd : B.blackBorder}`,
                cursor: "pointer",
              }}>{v.label}</button>
            ))}
          </div>

          {/* Progress */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 7, color: B.whiteMuted, fontFamily: "monospace", letterSpacing: 2 }}>COMPLETION</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: B.cfd, fontFamily: "monospace" }}>{progressPct}%</div>
            </div>
            <div style={{ width: 64, height: 3, background: B.blackBorder, borderRadius: 2 }}>
              <div style={{ width: `${progressPct}%`, height: "100%", background: `linear-gradient(90deg, ${B.cfd}, ${B.cfdLight})`, borderRadius: 2, transition: "width 0.5s" }} />
            </div>
            <div className="pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: B.cfd }} />
            <button className="hov" onClick={() => setSearchOpen(true)} title="Search (⌘K)" style={{ padding:"6px 10px", fontSize:9, letterSpacing:2, fontFamily:"monospace", background:"transparent", color:B.whiteGray, border:`1px solid ${B.blackBorder}`, cursor:"pointer" }}>🔍 SEARCH</button>
            <button className="hov" onClick={() => { setSageMasterProduct(null); setSageMasterOpen(true); }} style={{ padding:"6px 10px", fontSize:9, letterSpacing:2, fontFamily:"monospace", background:"transparent", color:"#4A9EFF", border:"1px solid #4A9EFF60", cursor:"pointer" }}>⚡ SAGE</button>
            <button className="hov" onClick={() => setShowLeaderboard(true)} style={{ padding:"6px 10px", fontSize:9, letterSpacing:2, fontFamily:"monospace", background:"transparent", color:"#2ECC71", border:"1px solid #2ECC7160", cursor:"pointer" }}>🏆 BOARD</button>
            <button className="hov" onClick={() => setSyncOpen(true)} style={{ padding:"6px 10px", fontSize:9, letterSpacing:2, fontFamily:"monospace", background:"transparent", color:B.cfd, border:`1px solid ${B.cfd}40`, cursor:"pointer" }}>📱 SYNC</button>
            <button className="hov" onClick={() => adminMode ? setView("admin") : setShowAdminLogin(true)} style={{ padding:"6px 10px", fontSize:9, letterSpacing:2, fontFamily:"monospace", background:view==="admin"?B.cfd:"transparent", color:view==="admin"?B.black:B.whiteMuted, border:`1px solid ${B.blackBorder}`, cursor:"pointer" }}>🔐</button>
          </div>
        </div>
        <GoldBar />
      </div>

      {/* ══ MAP VIEW ═══════════════════════════════════════════════════════════ */}
      {view === "map" && (
        <div style={{ maxWidth: 980, margin: "0 auto", padding: "28px 16px" }} className="fade-in">

          {/* Hero */}
          <div style={{ textAlign: "center", padding: "0 0 28px", borderBottom: `1px solid ${B.blackBorder}`, marginBottom: 28 }}>
            <div style={{ fontSize: 8, color: B.cfd, letterSpacing: 4, marginBottom: 10, fontFamily: "monospace" }}>
              ◈ FLEXX ACADEMY · FILE 3 OF 4 · THE PROFESSIONAL'S INSTRUMENT
            </div>
            <h1 style={{
              fontSize: "clamp(22px, 5vw, 40px)",
              fontWeight: 900, fontStyle: "italic",
              fontFamily: "'Arial Black', sans-serif",
              letterSpacing: 3, color: B.white,
              marginBottom: 10,
              textShadow: `0 0 40px ${B.cfdGlow}`,
            }}>
              CONTRACTS FOR DIFFERENCE
            </h1>
            <div style={{ fontSize: 11, color: B.whiteGray, fontFamily: "monospace", letterSpacing: 1.5, marginBottom: 16 }}>
              {COURSE.tagline}
            </div>
            <p style={{ fontSize: 13, color: B.whiteGray, lineHeight: 1.8, maxWidth: 660, margin: "0 auto", marginBottom: 20 }}>
              {COURSE.description}
            </p>
            {/* Quick stats */}
            <div style={{ display: "flex", justifyContent: "center", gap: 24, flexWrap: "wrap" }}>
              {[
                { label: "LEVELS", value: "3" },
                { label: "MODULES", value: `${totalMods}` },
                { label: "INSTRUMENTS", value: "5+" },
                { label: "STRATEGIES", value: "4" },
              ].map(s => (
                <div key={s.label} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: B.cfd, fontFamily: "monospace" }}>{s.value}</div>
                  <div style={{ fontSize: 7, color: B.whiteMuted, letterSpacing: 2, fontFamily: "monospace" }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Progress Dashboard */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
            {[
              { label: "PROGRESS", val: `${progressPct}%`, col: B.cfd },
              { label: "🔥 STREAK", val: `${streakData.streak}d`, col: "#FF6B35" },
              { label: "QUIZZES", val: `${Object.values(quizPassed).filter(Boolean).length}`, col: "#2ECC71" },
              { label: "DAYS ACTIVE", val: `${streakData.totalDays}`, col: "#4A9EFF" },
            ].map(stat => (
              <div key={stat.label} style={{ padding: "12px 14px", background: B.blackCard, border: `1px solid ${B.blackBorder}`, textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: stat.col, fontFamily: "monospace", lineHeight: 1.1 }}>{stat.val}</div>
                <div style={{ fontSize: 7, color: B.whiteMuted, letterSpacing: 2, fontFamily: "monospace", marginTop: 4 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Level tabs */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20, overflowX: "auto" }}>
            {COURSE.levels.map(lv => {
              const totalL = lv.phases.flatMap(p => p.modules).length;
              const doneL = lv.phases.flatMap(p => p.modules).filter(m => completed[m.id]).length;
              return (
                <button key={lv.id} className="hov" onClick={() => { setLevel(lv.id); setPhaseIdx(0); }} style={{
                  padding: "12px 20px", fontSize: 9, letterSpacing: 2, fontFamily: "monospace", fontWeight: 700,
                  whiteSpace: "nowrap", cursor: "pointer", transition: "all 0.2s",
                  background: level === lv.id ? `linear-gradient(135deg, ${lv.color}, ${lv.color}99)` : "transparent",
                  color: level === lv.id ? B.black : lv.color,
                  border: `1px solid ${level === lv.id ? lv.color : lv.color + "40"}`,
                }}>
                  <span style={{ marginRight: 6, fontSize: 12 }}>{lv.icon}</span>
                  {lv.label}
                  <span style={{ marginLeft: 8, fontSize: 7, opacity: 0.7 }}>· {lv.tag}</span>
                  <span style={{ marginLeft: 8, fontSize: 8, opacity: 0.6 }}>({doneL}/{totalL})</span>
                </button>
              );
            })}
          </div>

          {/* Level description */}
          {currentLevel && (
            <div style={{
              padding: "16px 20px", marginBottom: 20,
              background: B.blackCard, border: `1px solid ${B.blackBorder}`,
              borderLeft: `3px solid ${currentLevel.color}`,
            }}>
              <div style={{ fontSize: 9, color: currentLevel.color, letterSpacing: 2, marginBottom: 5, fontFamily: "monospace" }}>{currentLevel.tag}</div>
              <div style={{ fontSize: 13, color: B.whiteGray, lineHeight: 1.7 }}>{currentLevel.desc}</div>
            </div>
          )}

          {/* Phase tabs */}
          <div style={{ display: "flex", gap: 6, marginBottom: 20, overflowX: "auto" }}>
            {currentLevel?.phases.map((ph, i) => {
              const { pct } = phaseProgress(ph);
              return (
                <button key={ph.id} className="hov" onClick={() => setPhaseIdx(i)} style={{
                  padding: "8px 14px", fontSize: 8, letterSpacing: 1, fontFamily: "monospace",
                  whiteSpace: "nowrap", cursor: "pointer", transition: "all 0.2s",
                  background: phaseIdx === i ? `linear-gradient(135deg, ${B.cfd}, ${B.cfdDark})` : "transparent",
                  color: phaseIdx === i ? B.black : B.whiteGray,
                  border: `1px solid ${phaseIdx === i ? B.cfd : B.blackBorder}`,
                }}>
                  {ph.icon} {ph.label}
                  <span style={{ marginLeft: 6, opacity: 0.65 }}>{pct}%</span>
                </button>
              );
            })}
          </div>

          {/* Phase detail */}
          {currentPhase && (
            <div className="fade-in">
              <div style={{
                padding: "18px 20px", marginBottom: 16,
                background: B.blackCard, border: `1px solid ${B.blackBorder}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 7, color: B.cfd, letterSpacing: 3, marginBottom: 5, fontFamily: "monospace" }}>
                      {currentPhase.weeks} · {currentPhase.modules.length} LESSONS
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: B.white, marginBottom: 6 }}>{currentPhase.label}</div>
                    <div style={{ fontSize: 12, color: B.whiteGray, lineHeight: 1.65, maxWidth: 560 }}>{currentPhase.desc}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {(() => { const { done, total, pct } = phaseProgress(currentPhase); return (
                      <>
                        <div style={{ fontSize: 8, color: B.whiteMuted, fontFamily: "monospace" }}>{done}/{total} COMPLETE · {pct}%</div>
                        <div style={{ width: 90, height: 2, background: B.blackBorder, marginTop: 6 }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: B.cfd, transition: "width 0.3s" }} />
                        </div>
                      </>
                    ); })()}
                  </div>
                </div>
              </div>

              {/* Module cards */}
              <div style={{ display: "grid", gap: 6 }}>
                {currentPhase.modules.map((mod, idx) => (
                  <div key={mod.id} className="mod-card hov" onClick={() => openLesson(mod, phaseIdx)} style={{
                    padding: "14px 18px", cursor: "pointer",
                    background: completed[mod.id] ? B.cfdFaint : B.blackCard,
                    border: `1px solid ${completed[mod.id] ? B.cfd + "30" : B.blackBorder}`,
                    transition: "all 0.2s",
                  }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                      {/* Number/check */}
                      <div style={{
                        width: 32, height: 32, flexShrink: 0,
                        border: `1px solid ${completed[mod.id] ? B.cfd : B.blackBorder}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 11, fontFamily: "monospace",
                        color: completed[mod.id] ? B.cfd : B.whiteMuted,
                        background: completed[mod.id] ? B.cfdFaint : "transparent",
                      }}>
                        {completed[mod.id] ? "✓" : String(idx + 1).padStart(2, "0")}
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: B.white }}>{mod.title}</span>
                          <TypeBadge type={mod.type} />
                          <Tag size={7}>{mod.tag}</Tag>
                        </div>
                        <div style={{ fontSize: 11, color: B.whiteMuted, lineHeight: 1.55 }}>
                          {mod.summary.slice(0, 120)}...
                        </div>
                      </div>

                      {/* Meta */}
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                        <span style={{ fontSize: 9, color: B.whiteMuted, fontFamily: "monospace" }}>⏱ {mod.duration}</span>
                        <span style={{ color: B.cfd, fontSize: 18, lineHeight: 1 }}>›</span>
                      </div>
                    </div>

                    {/* Note preview */}
                    {notes[mod.id] && (
                      <div style={{
                        marginTop: 8, paddingTop: 6, borderTop: `1px solid ${B.blackBorder}`,
                        fontSize: 9, color: B.whiteMuted, fontFamily: "monospace",
                      }}>
                        📝 {notes[mod.id].slice(0, 80)}{notes[mod.id].length > 80 ? "..." : ""}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ LESSON VIEW ════════════════════════════════════════════════════════ */}
      {view === "lesson" && activeLesson && (
        <div style={{ maxWidth: 840, margin: "0 auto", padding: "24px 16px" }} className="fade-in">

          {/* Lesson header */}
          <div style={{
            padding: "24px 24px 20px",
            marginBottom: 20,
            background: B.blackCard,
            border: `1px solid ${B.blackBorder}`,
            borderLeft: `4px solid ${B.cfd}`,
          }}>
            {/* Breadcrumb */}
            <div style={{ fontSize: 8, color: B.whiteMuted, letterSpacing: 2, marginBottom: 10, fontFamily: "monospace", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span style={{ color: B.cfd }}>CFD MASTERY</span>
              <span>·</span>
              <span>{COURSE.levels.find(l => l.id === activeLesson.levelId)?.label}</span>
              <span>·</span>
              <span>{COURSE.levels.find(l => l.id === activeLesson.levelId)?.phases.find(p => p.id === activeLesson.phaseId)?.label}</span>
              <span>·</span>
              <span>{activeLesson.duration}</span>
            </div>

            <h2 style={{ fontSize: "clamp(16px, 3vw, 22px)", fontWeight: 700, color: B.white, marginBottom: 10, lineHeight: 1.3 }}>
              {activeLesson.title}
            </h2>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              <TypeBadge type={activeLesson.type} />
              <Tag size={8}>{activeLesson.tag}</Tag>
            </div>

            <p style={{ fontSize: 13, color: B.whiteGray, lineHeight: 1.8, marginBottom: 16 }}>{activeLesson.summary}</p>

            <button className="hov" onClick={() => toggleDone(activeLesson.id)} style={{
              padding: "9px 22px", fontSize: 9, letterSpacing: 2, fontFamily: "monospace", fontWeight: 700,
              background: completed[activeLesson.id]
                ? `linear-gradient(135deg, ${B.cfd}, ${B.cfdDark})`
                : "transparent",
              color: completed[activeLesson.id] ? B.black : B.cfd,
              border: `1px solid ${B.cfd}`,
              cursor: "pointer",
            }}>
              {completed[activeLesson.id] ? "✓ LESSON COMPLETE" : "MARK AS COMPLETE"}
            </button>
          </div>

          {/* Why It Matters */}
          {activeLesson.whyItMatters && (
            <div style={{
              padding: "14px 18px", marginBottom: 16,
              background: B.cfdFaint,
              border: `1px solid ${B.cfd}20`,
              borderLeft: `3px solid ${B.cfd}`,
            }}>
              <div style={{ fontSize: 8, color: B.cfd, letterSpacing: 2, marginBottom: 6, fontFamily: "monospace" }}>◆ WHY THIS MATTERS</div>
              <p style={{ fontSize: 12, color: B.whiteGray, lineHeight: 1.7 }}>{activeLesson.whyItMatters}</p>
            </div>
          )}

          {/* Danger Zone */}
          {activeLesson.dangerZone && (
            <div style={{
              padding: "14px 18px", marginBottom: 16,
              background: B.cfdWarnFaint,
              border: `1px solid ${B.cfdWarn}30`,
              borderLeft: `3px solid ${B.cfdWarn}`,
            }}>
              <div style={{ fontSize: 8, color: B.cfdWarn, letterSpacing: 2, marginBottom: 6, fontFamily: "monospace" }}>⚠ DANGER ZONE — READ CAREFULLY</div>
              <p style={{ fontSize: 12, color: B.whiteGray, lineHeight: 1.7 }}>{activeLesson.dangerZone}</p>
            </div>
          )}

          {/* Key Points — Expandable */}
          <div style={{ marginBottom: 16 }}>
            <button className="expand-btn hov" onClick={() => setExpandedSection(s => s === "kp" ? null : "kp")} style={{
              width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "12px 18px", background: B.blackCard, border: `1px solid ${B.blackBorder}`,
              color: B.white, cursor: "pointer", fontSize: 10, letterSpacing: 2, fontFamily: "monospace",
            }}>
              <span style={{ color: B.cfd }}>◆ KEY CONCEPTS ({activeLesson.keyPoints.length})</span>
              <span style={{ color: B.whiteMuted, fontSize: 14 }}>{expandedSection === "kp" ? "−" : "+"}</span>
            </button>
            {expandedSection === "kp" && (
              <div className="fade-in" style={{ border: `1px solid ${B.blackBorder}`, borderTop: "none" }}>
                {activeLesson.keyPoints.map((pt, i) => (
                  <div key={i} style={{
                    display: "flex", gap: 12, padding: "13px 18px",
                    borderBottom: i < activeLesson.keyPoints.length - 1 ? `1px solid ${B.blackBorder}` : "none",
                    background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)",
                  }}>
                    <div style={{ color: B.cfd, flexShrink: 0, marginTop: 3, fontSize: 10, fontWeight: 700 }}>›</div>
                    <div style={{ fontSize: 13, color: B.whiteGray, lineHeight: 1.7 }}>{pt}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Practice Task */}
          <div style={{
            padding: "16px 18px", marginBottom: 16,
            background: B.cfdFaint,
            border: `1px solid ${B.cfd}25`,
          }}>
            <div style={{ fontSize: 8, color: B.cfd, letterSpacing: 2, marginBottom: 8, fontFamily: "monospace" }}>◆ YOUR PRACTICE TASK</div>
            <p style={{ fontSize: 13, color: B.whiteGray, lineHeight: 1.75, whiteSpace: "pre-line" }}>{activeLesson.practice}</p>
          </div>

          {/* Resources — Expandable */}
          <div style={{ marginBottom: 16 }}>
            <button className="expand-btn hov" onClick={() => setExpandedSection(s => s === "res" ? null : "res")} style={{
              width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "12px 18px", background: B.blackCard, border: `1px solid ${B.blackBorder}`,
              color: B.white, cursor: "pointer", fontSize: 10, letterSpacing: 2, fontFamily: "monospace",
            }}>
              <span style={{ color: B.cfd }}>◆ RESOURCES ({activeLesson.resources.length})</span>
              <span style={{ color: B.whiteMuted, fontSize: 14 }}>{expandedSection === "res" ? "−" : "+"}</span>
            </button>
            {expandedSection === "res" && (
              <div className="fade-in" style={{ display: "grid", gap: 4, borderTop: "none" }}>
                {activeLesson.resources.map((r, i) => (
                  <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" className="res-card hov" style={{
                    display: "flex", gap: 12, padding: "13px 18px",
                    background: B.blackCard,
                    border: `1px solid ${B.blackBorder}`,
                    textDecoration: "none", transition: "all 0.2s", alignItems: "center",
                    borderTop: "none",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: B.white, fontWeight: 600, marginBottom: 2 }}>{r.label}</div>
                      <div style={{ fontSize: 8, color: B.whiteMuted, fontFamily: "monospace", letterSpacing: 1 }}>{r.type}</div>
                    </div>
                    <Tag size={7} color={B.cfd}>{r.tag}</Tag>
                    <span style={{ color: B.cfd, fontSize: 14, flexShrink: 0 }}>↗</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 8, color: B.cfd, letterSpacing: 3, marginBottom: 10, fontFamily: "monospace" }}>◆ YOUR NOTES</div>
            <textarea
              value={noteInput}
              onChange={e => setNoteInput(e.target.value)}
              placeholder="Key insights, calculation results, observations from your practice..."
              style={{
                width: "100%", minHeight: 100, padding: 14,
                background: B.blackCard, border: `1px solid ${B.blackBorder}`,
                color: B.whiteGray, fontSize: 12, fontFamily: "Georgia, serif",
                resize: "vertical", outline: "none", lineHeight: 1.7,
              }}
            />
            <button className="hov" onClick={saveNote} style={{
              marginTop: 8, padding: "7px 18px", fontSize: 8, letterSpacing: 2, fontFamily: "monospace",
              background: "transparent", color: B.cfd, border: `1px solid ${B.cfd + "40"}`, cursor: "pointer",
            }}>SAVE NOTES</button>
          </div>

          {/* Video Embed */}
          {videoUrls[activeLesson.id] && getYTEmbed(videoUrls[activeLesson.id]) && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 8, color: B.cfd, letterSpacing: 3, marginBottom: 10, fontFamily: "monospace" }}>◆ VIDEO LESSON</div>
              <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
                <iframe src={getYTEmbed(videoUrls[activeLesson.id])} title="Lesson Video"
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </div>
          )}

          {/* SageMaster Callout */}
          <div style={{ padding: "14px 18px", marginBottom: 22, background: "#0A1628", border: "1px solid #4A9EFF30", borderLeft: "3px solid #4A9EFF" }}>
            <div style={{ fontSize: 8, color: "#4A9EFF", letterSpacing: 2, marginBottom: 8, fontFamily: "monospace" }}>⚡ SAGEMASTER AI TOOLS FOR THIS LESSON</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {[
                { name: "AI Screener", href: "https://sagemaster.com/product/ai-screener" },
                { name: "DeepSage", href: "https://sagemaster.com/product/deepsage" },
                { name: "AI Grid", href: "https://sagemaster.com/product/ai-grid" },
              ].map(tool => (
                <a key={tool.name} href={tool.href} target="_blank" rel="noopener noreferrer" style={{
                  padding: "6px 12px", fontSize: 9, letterSpacing: 1, fontFamily: "monospace",
                  color: "#4A9EFF", border: "1px solid #4A9EFF40", textDecoration: "none", background: "#4A9EFF08",
                }}>{tool.name} ↗</a>
              ))}
            </div>
          </div>

          {/* Community Thread */}
          <LessonThread moduleId={activeLesson.id} />

          {/* Quiz Prompt */}
          {MODULE_QUIZZES[activeLesson.id] && !quizPassed[activeLesson.id] && (
            <div style={{ padding: "14px 18px", marginBottom: 22, background: B.goldFaint, border: `1px solid ${B.gold}30`, borderLeft: `3px solid ${B.gold}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: B.whiteGray }}>Test what you learned — pass to earn certificate</div>
              <button className="hov" onClick={() => startQuiz(activeLesson.id, activeLesson.title)} style={{
                padding: "8px 16px", fontSize: 9, letterSpacing: 2, fontFamily: "monospace",
                background: B.gold, color: B.black, border: "none", cursor: "pointer",
              }}>TAKE QUIZ →</button>
            </div>
          )}
          {quizPassed[activeLesson.id] && (
            <div style={{ padding: "12px 18px", marginBottom: 22, background: "#0A2010", border: "1px solid #2ECC7130", borderLeft: "3px solid #2ECC71", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: "#2ECC71" }}>✓ Quiz passed — certificate available</div>
              <button className="hov" onClick={() => generateCert(lbName || "Trader", activeLesson.title, "CFD TRADING")} style={{
                padding: "7px 14px", fontSize: 9, letterSpacing: 2, fontFamily: "monospace",
                background: "transparent", color: "#2ECC71", border: "1px solid #2ECC7150", cursor: "pointer",
              }}>VIEW CERT</button>
            </div>
          )}

          {/* Navigation */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            paddingTop: 18, borderTop: `1px solid ${B.blackBorder}`,
          }}>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="hov" onClick={() => setView("map")} style={{
                padding: "9px 16px", fontSize: 8, letterSpacing: 2, fontFamily: "monospace",
                background: "transparent", color: B.whiteMuted, border: `1px solid ${B.blackBorder}`, cursor: "pointer",
              }}>← MAP</button>
              {ALL_MODS.findIndex(m => m.id === activeLesson.id) > 0 && (
                <button className="hov" onClick={navPrev} style={{
                  padding: "9px 16px", fontSize: 8, letterSpacing: 2, fontFamily: "monospace",
                  background: "transparent", color: B.whiteMuted, border: `1px solid ${B.blackBorder}`, cursor: "pointer",
                }}>‹ PREV</button>
              )}
            </div>
            {(() => {
              const cur = ALL_MODS.findIndex(m => m.id === activeLesson.id);
              const next = ALL_MODS[cur + 1];
              if (!next) return <div style={{ fontSize: 10, color: B.cfd, fontFamily: "monospace" }}>🏆 COURSE COMPLETE!</div>;
              return (
                <button className="hov" onClick={navNext} style={{
                  padding: "9px 18px", fontSize: 8, letterSpacing: 2, fontFamily: "monospace",
                  background: "transparent", color: B.cfd, border: `1px solid ${B.cfd + "40"}`, cursor: "pointer",
                }}>NEXT: {next.title.slice(0, 40)}... ›</button>
              );
            })()}
          </div>
        </div>
      )}

      {/* ══ GLOSSARY VIEW ══════════════════════════════════════════════════════ */}
      {view === "glossary" && (
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 16px" }} className="fade-in">
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 8, color: B.cfd, letterSpacing: 4, marginBottom: 10, fontFamily: "monospace" }}>◆ CFD TRADING GLOSSARY</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: B.white, marginBottom: 6 }}>Key Terms & Definitions</h2>
            <p style={{ fontSize: 12, color: B.whiteGray, lineHeight: 1.6, marginBottom: 16 }}>
              Every professional term you'll encounter in CFD trading — defined clearly and concisely.
            </p>
            <input
              type="text"
              placeholder="Search glossary..."
              value={glossaryFilter}
              onChange={e => setGlossaryFilter(e.target.value)}
              style={{
                width: "100%", padding: "10px 16px",
                background: B.blackCard, border: `1px solid ${B.blackBorder}`,
                color: B.whiteGray, fontSize: 12, fontFamily: "Georgia, serif",
                outline: "none",
              }}
            />
          </div>

          <div style={{ display: "grid", gap: 4 }}>
            {GLOSSARY.map((g, i) => (
              <div key={i} style={{
                display: "flex", gap: 16, padding: "13px 18px",
                background: i % 2 === 0 ? B.blackCard : "transparent",
                border: `1px solid ${B.blackBorder}`,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: B.cfd,
                  fontFamily: "monospace", flexShrink: 0, minWidth: 120,
                  lineHeight: 1.6,
                }}>{g.term}</div>
                <div style={{ fontSize: 12, color: B.whiteGray, lineHeight: 1.7 }}>{g.def}</div>
              </div>
            ))}
            {GLOSSARY.length === 0 && (
              <div style={{ padding: "24px", textAlign: "center", color: B.whiteMuted, fontSize: 12 }}>
                No terms match "{glossaryFilter}"
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── FOOTER ─────────────────────────────────────────────────────────── */}
      <div style={{ borderTop: `1px solid ${B.blackBorder}`, marginTop: 56 }}>
        <GoldBar />
        <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 7, color: B.whiteMuted, fontFamily: "monospace", letterSpacing: 2 }}>
            FLEXX ACADEMY · DUBAI · 2026 · FILE 3 OF 4
          </div>
          <div style={{ display: "flex", gap: 16 }}>
            {["FILE 1: CRYPTO & STOCKS", "FILE 2: FOREX & INDICES", "FILE 3: CFD ← YOU ARE HERE", "FILE 4: ADVANCED SYSTEMS"].map((f, i) => (
              <div key={i} style={{
                fontSize: 7, fontFamily: "monospace", letterSpacing: 1,
                color: i === 2 ? B.cfd : B.whiteDim,
              }}>{f}</div>
            ))}
          </div>
          <div style={{ fontSize: 7, color: B.whiteMuted, fontFamily: "monospace", letterSpacing: 2 }}>
            CFD TRADING INVOLVES SIGNIFICANT RISK · EDUCATIONAL USE ONLY
          </div>
        </div>
      </div>
    </div>
  );
}
